// Stories posted straight to Stories: the tray, a full-screen viewer, a sticker editor,
// Close Friends, Highlights and the archive. Every read and write goes through the
// server's /api/story-items routes; the server decides who may see what.
(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root) root.LouderrStoriesV2 = api;
})(typeof window === "undefined" ? null : window, function () {
  "use strict";

  const TEXT_COLORS = Object.freeze({
    white: "#ffffff", black: "#111114", yellow: "#ffd60a", pink: "#ff6fae", blue: "#5ac8fa", green: "#34c759",
  });
  const TEXT_STYLES = Object.freeze(["plain", "box", "outline"]);
  const STICKER_EMOJI = Object.freeze([
    "\u{1F525}", "\u{1F602}", "\u{1F60D}", "\u{1F3B6}", "✨", "\u{1F44F}", "\u{1F64C}", "\u{1F4AF}",
    "\u{1F62E}", "\u{1F622}", "❤️", "\u{1F440}", "\u{1F3A7}", "\u{1F3A4}", "\u{1F319}", "☀️",
  ]);
  const QUICK_REACTIONS = Object.freeze(["❤️", "\u{1F525}", "\u{1F602}", "\u{1F62E}", "\u{1F622}", "\u{1F44F}"]);
  const REPORT_CATEGORIES = Object.freeze([
    ["child_safety", "A child's safety"], ["credible_threat", "A threat of violence"],
    ["self_harm", "Self-harm or suicide"], ["ncii", "Intimate images shared without consent"],
    ["hate_or_harassment", "Hate or harassment"], ["sexual_content", "Sexual content"],
    ["impersonation", "Impersonation"], ["privacy_or_doxxing", "Private information"],
    ["spam_or_scam", "Spam or a scam"], ["malware_or_phishing", "A harmful link"], ["other", "Something else"],
  ]);
  const IMAGE_SECONDS = 6;
  const MAX_ELEMENTS = 12;
  const MAX_HIGHLIGHT_ITEMS = 100;
  const UPLOAD_POLL_MS = 1500;
  const UPLOAD_WAIT_MS = 180000;

  function clamp(value, low, high) {
    const number = Number(value);
    return Number.isFinite(number) ? Math.min(high, Math.max(low, number)) : low;
  }

  function relativeAge(iso, now = Date.now()) {
    const then = Date.parse(iso || "");
    if (!Number.isFinite(then)) return "";
    const minutes = Math.max(0, Math.floor((now - then) / 60000));
    if (minutes < 1) return "now";
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return hours < 24 ? `${hours}h` : `${Math.floor(hours / 24)}d`;
  }

  function itemDurationMs(item) {
    if (item?.media?.type === "video") {
      const ms = Number(item.media.duration_ms);
      return Number.isFinite(ms) && ms > 0 ? Math.min(60000, ms) : 15000;
    }
    return IMAGE_SECONDS * 1000;
  }

  function stickerPayload(elements) {
    return (elements || []).slice(0, MAX_ELEMENTS).map(element => {
      const base = {
        type: element.type,
        x: Math.round(clamp(element.x, 0, 1) * 10000) / 10000,
        y: Math.round(clamp(element.y, 0, 1) * 10000) / 10000,
        scale: Math.round(clamp(element.scale ?? 1, 0.4, 3) * 100) / 100,
        rotation: Math.round(clamp(element.rotation ?? 0, -180, 180)),
      };
      if (element.type === "text") return { ...base, text: element.text, color: element.color, style: element.style };
      if (element.type === "emoji") return { ...base, emoji: element.emoji };
      if (element.type === "mention") return { ...base, user_id: element.user_id };
      if (element.type === "link") return { ...base, url: element.url, label: element.label || "" };
      if (element.type === "poll") return { ...base, question: element.question, options: element.options.slice(0, 2) };
      return base;
    });
  }

  function validateDraft(draft) {
    const elements = draft.elements || [];
    if (!draft.file) return "Choose a photo or video first.";
    if (elements.length > MAX_ELEMENTS) return `A Story can hold up to ${MAX_ELEMENTS} stickers.`;
    if (elements.filter(e => e.type === "poll").length > 1) return "A Story can hold one poll.";
    if (elements.filter(e => e.type === "link").length > 1) return "A Story can hold one link.";
    if (elements.filter(e => e.type === "mention").length > 5) return "A Story can mention up to 5 people.";
    if ((draft.caption || "").length > 200) return "A caption can be up to 200 characters.";
    return "";
  }

  const STORY_VIDEO_MS = 60000;
  const pollCountsValid = poll => Array.isArray(poll?.counts) && poll.counts.length === 2
    && poll.counts.every(count => Number.isSafeInteger(count) && count >= 0)
    && Number.isSafeInteger(poll.counts[0] + poll.counts[1]);
  const pollConfirmed = poll => pollCountsValid(poll) && [0, 1].includes(poll.viewer_vote) && poll.counts[poll.viewer_vote] > 0;
  const UPLOAD_LIMIT_BYTES = 25 * 1024 * 1024;

  const RECOVERY_VERSION = 1, RECOVERY_LIMIT = 4;
  const recoveryError = message => Object.assign(new Error(message), { storyStorage: true });
  function validRecovery(value, owner = value?.owner) {
    return value?.version === RECOVERY_VERSION && value.owner === owner && typeof owner === "string" && Boolean(owner)
      && typeof value.revision === "string" && Boolean(value.revision)
      && typeof value.publishToken === "string" && Boolean(value.publishToken)
      && typeof value.uploadKey === "string" && Boolean(value.uploadKey)
      && ["draft", "publish", "remove"].includes(value.action) && typeof value.shared === "boolean"
      && typeof value.uploadStarted === "boolean" && Number.isFinite(value.savedAt)
      && [value.uploadId, value.mediaId].every(id => id === null || (typeof id === "string" && Boolean(id)))
      && (!value.shared || Boolean(value.mediaId))
      && (!value.mediaId || (value.uploadStarted && value.mediaId === value.uploadId))
      && (value.action !== "draft" || (!value.shared && !value.uploadStarted && value.uploadId === null && value.mediaId === null))
      && value.draft && ["public", "close_friends"].includes(value.draft.audience)
      && typeof value.draft.caption === "string" && Array.isArray(value.draft.elements)
      && value.draft.elements.every(item => item && ["text", "emoji", "mention", "link", "poll"].includes(item.type)
        && (item.type !== "poll" || (Array.isArray(item.options) && item.options.length === 2 && item.options.every(option => typeof option === "string"))))
      && Number.isFinite(value.draft.file?.size) && value.draft.file.size > 0
      && !storyFileProblem(value.draft.file) && !validateDraft(value.draft);
  }

  // Separate from Studio's schema. Keep the actual file and request identity on this
  // device before dispatch. A read failure is never interpreted as an empty journal.
  function publicationStore(factory = typeof indexedDB === "undefined" ? null : indexedDB) {
    const key = (owner, token) => `${owner}:${token}`;
    async function operation(mode, action) {
      if (!factory) throw recoveryError("This browser cannot keep Story recovery copies. Enable browser storage and try again.");
      const db = await new Promise((resolve, reject) => {
        let request, failed = false;
        try { request = factory.open("louderr-story-publications", 1); }
        catch (_) { reject(recoveryError("Story recovery storage could not open.")); return; }
        request.onupgradeneeded = () => {
          if (!request.result.objectStoreNames.contains("publications")) request.result.createObjectStore("publications");
        };
        request.onsuccess = () => {
          if (failed) { request.result.close(); return; }
          request.result.onversionchange = () => request.result.close();
          resolve(request.result);
        };
        request.onerror = request.onblocked = () => {
          failed = true; reject(recoveryError("Story recovery storage is unavailable or busy in another tab."));
        };
      });
      try {
        return await new Promise((resolve, reject) => {
          let transaction, result, problem;
          const fail = message => { problem = recoveryError(message); transaction.abort(); };
          try {
            transaction = db.transaction("publications", mode);
            transaction.oncomplete = () => resolve(result);
            transaction.onabort = transaction.onerror = () => reject(problem || recoveryError("The Story recovery copy could not be saved. Check available browser storage and try again."));
            action(transaction.objectStore("publications"), value => { result = value; }, fail);
          } catch (_) {
            if (transaction) { try { transaction.abort(); } catch (_) {} }
            reject(recoveryError("The Story recovery copy could not be read or saved."));
          }
        });
      } finally { db.close(); }
    }
    return {
      list: owner => operation("readonly", (store, done, fail) => {
        const request = store.openCursor();
        const rows = [];
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) { done(rows.sort((a, b) => b.savedAt - a.savedAt)); return; }
          if (typeof cursor.key === "string" && cursor.key.startsWith(`${owner}:`)) {
            if (!validRecovery(cursor.value, owner) || cursor.key !== key(owner, cursor.value.publishToken)) {
              fail("A Story recovery copy is unreadable. It has been preserved."); return;
            }
            rows.push(cursor.value);
          }
          cursor.continue();
        };
      }),
      compare: (owner, token, revision, value, allowed = () => true) => operation("readwrite", (store, done, fail) => {
        if (!owner || !token || !allowed()) { fail("The Story account changed. Nothing new was sent."); return; }
        if (value && (!validRecovery(value, owner) || value.publishToken !== token)) { fail("The Story recovery copy is invalid."); return; }
        const request = store.get(key(owner, token));
        request.onsuccess = () => {
          const previous = request.result;
          if (!allowed()) { fail("The Story account changed. Nothing new was sent."); return; }
          if ((previous != null && !validRecovery(previous, owner)) || (previous?.revision ?? null) !== revision) {
            fail("This recovery copy changed in another tab. Open Recover unfinished Story to load the current copy."); return;
          }
          if (!value) { store.delete(key(owner, token)); done(null); return; }
          const keys = store.getAllKeys();
          keys.onsuccess = () => {
            if (!allowed()) { fail("The Story account changed. Nothing new was sent."); return; }
            if (!previous && keys.result.filter(item => typeof item === "string" && item.startsWith(`${owner}:`)).length >= RECOVERY_LIMIT) {
              fail("Four unfinished Stories are kept on this device. Recover or forget one before sharing another."); return;
            }
            store.put(value, key(owner, token)); done(value.revision);
          };
        };
      }),
      clear: () => operation("readwrite", store => { store.clear(); }),
    };
  }

  // Does media of this shape fill the stage without losing much of the picture? A phone screenshot does on
  // a phone and does not on a wide screen; there the whole picture is shown over a blurred copy of itself.
  function fitsStage(width, height, stageRatio = 9 / 16, tolerance = 0.12) {
    const w = Number(width);
    const h = Number(height);
    const s = Number(stageRatio);
    if (!(w > 0 && h > 0 && s > 0)) return true;
    return Math.abs(w / h - s) / s <= tolerance;
  }

  // What stops this file before anything is uploaded. Empty when it can be posted.
  function storyFileProblem(file, { durationMs = null, maxBytes = UPLOAD_LIMIT_BYTES } = {}) {
    const type = String(file?.type || "");
    if (!file || !(type.startsWith("image/") || type.startsWith("video/"))) return "Choose a photo or video.";
    if (Number(file.size) > maxBytes) {
      return `This file is larger than ${Math.round(maxBytes / 1048576)} MB. Post it as a Drop instead, which can make it smaller first.`;
    }
    if (type.startsWith("video/") && Number(durationMs) > STORY_VIDEO_MS + 500) {
      return `Stories can be up to 60 seconds. This video is ${Math.round(Number(durationMs) / 1000)} seconds.`;
    }
    return "";
  }

  function orderTray(entries) {
    const own = entries.filter(entry => entry.is_own);
    const unseen = entries.filter(entry => !entry.is_own && entry.has_unseen);
    const seen = entries.filter(entry => !entry.is_own && !entry.has_unseen);
    return [...own, ...unseen, ...seen];
  }

  function safeUrl(value) {
    try {
      const url = new URL(value);
      return url.protocol === "https:" || url.protocol === "http:" ? url.href : "";
    } catch (_) { return ""; }
  }

  function create(hooks) {
    const doc = hooks.document || document;
    const state = { tray: [], trayViewer: null, trayRequest: 0, viewer: null, composer: null, posting: null, postingSheet: null };
    const win = hooks.window || (typeof window === "undefined" ? null : window);
    const recovery = hooks.publicationStore || publicationStore();
    // Window timers require a Window receiver in Firefox/WebKit. Storing the
    // unbound globals in an object and then calling them as methods makes the
    // Story editor throw "Illegal invocation" after a capture. Keep injected
    // clocks untouched for deterministic tests, but bind the browser clock to
    // the active window for the real editor.
    const draftClock = hooks.draftClock || (win
      ? { setTimeout: win.setTimeout.bind(win), clearTimeout: win.clearTimeout.bind(win) }
      : { setTimeout, clearTimeout });
    const recoveryWrites = new Set();
    let recoveryReset = Promise.resolve();
    let leaveGuard = null;
    const leaveReasons = new Set();

    const node = (tag, className = "", text = "") => {
      const element = doc.createElement(tag);
      if (className) element.className = className;
      if (text !== "") element.textContent = text;
      return element;
    };
    const svg = name => {
      const holder = node("span", "sv-icon");
      holder.innerHTML = `<svg aria-hidden="true"><use href="#icon-${name}"></use></svg>`;
      return holder;
    };
    const button = (label, className, handler, iconName = "", visibleText = "") => {
      const element = node("button", className);
      element.type = "button";
      element.setAttribute("aria-label", label);
      if (iconName) element.append(svg(iconName));
      if (visibleText) element.append(node("span", "", visibleText));
      else if (!iconName) element.textContent = label;
      element.addEventListener("click", event => { event.stopPropagation(); handler(event); });
      return element;
    };
    const viewerId = () => hooks.viewer()?.id || null;
    const canWrite = () => Boolean(hooks.canWrite?.());
    const say = (message, error = false) => hooks.toast?.(message, error);
    const replyRecords = new Map();
    const pollRecords = new Map(), pollListeners = new Set();
    let pollOwner = null;
    function pollAttempt(item) {
      if (pollOwner !== viewerId()) { pollRecords.clear(); pollOwner = viewerId(); }
      return pollRecords.get(item.id) || null;
    }
    const notifyPoll = id => { for (const listener of [...pollListeners]) listener(id); };
    const accountSheets = new Set();
    const highlightCreates = new Map();
    const overlays = [];
    let highlightCreateOwner = null;
    let archivePanel = null;
    let createRequest = 0, createOwner = null, filePicker = null;
    let postingOwner = viewerId(), postingEpoch = 0;
    const postingAccount = job => job.owner === viewerId() && job.epoch === postingEpoch;
    let replyOwner = null;

    function replyRecord(current) {
      if (replyOwner !== viewerId()) {
        replyRecords.clear();
        replyOwner = viewerId();
      }
      if (!replyRecords.has(current.id)) replyRecords.set(current.id, {
        draft: "", attempt: null, busy: false, message: "", paint: null,
      });
      return replyRecords.get(current.id);
    }

    function avatar(user, className = "sv-avatar") {
      const element = node("span", `avatar ${className}`);
      hooks.styleAvatar?.(element, user);
      return element;
    }

    // A modal <dialog>, so it stacks above the composer and Settings, which are modal too.
    function overlay(className, label) {
      const shell = node("dialog", `sv-overlay ${className}`);
      shell.setAttribute("aria-label", label);
      const previous = doc.activeElement, owner = viewerId(), focusFrames = new Set();
      let dismissed = false;
      shell.isCurrent = () => !dismissed && overlays.at(-1) === shell && viewerId() === owner;
      const available = target => target && target.isConnected !== false && !target.disabled && !target.hidden
        && !target.closest?.("[hidden], [inert]");
      shell.focusLater = target => {
        const frame = requestAnimationFrame(() => {
          focusFrames.delete(frame);
          const activeDialog = doc.activeElement?.closest?.("dialog");
          if (shell.isCurrent() && (!activeDialog || activeDialog === shell || !activeDialog.open)
            && available(target) && (shell.contains?.(target) ?? true)) target.focus({ preventScroll: true });
        });
        focusFrames.add(frame);
      };
      shell.addEventListener("cancel", event => {
        event.preventDefault();
        shell.dispatchEvent(new CustomEvent("sv-close"));
      });
      shell.dismiss = () => {
        if (dismissed) return;
        const activeDialog = doc.activeElement?.closest?.("dialog");
        const wasCurrent = shell.isCurrent() && (!activeDialog || activeDialog === shell || !activeDialog.open);
        dismissed = true;
        for (const frame of focusFrames) cancelAnimationFrame(frame);
        focusFrames.clear();
        overlays.splice(overlays.indexOf(shell), 1);
        if (shell.open && typeof shell.close === "function") shell.close();
        shell.remove();
        if (!wasCurrent || viewerId() !== owner) return;
        const parent = overlays.at(-1);
        const target = shell.returnFocus?.() || previous;
        const dialog = target?.closest?.("dialog");
        if (available(target) && typeof target.focus === "function" && (!dialog || dialog.open)
          && (!parent || (parent.contains?.(target) ?? false))) target.focus({ preventScroll: true });
        else if (parent?.isCurrent()) parent.focus({ preventScroll: true });
      };
      doc.body.append(shell);
      overlays.push(shell);
      if (typeof shell.showModal === "function") shell.showModal();
      else shell.setAttribute("open", "");
      return shell;
    }

    function sheet(title, label = title) {
      const shell = overlay("sv-sheet-overlay", label);
      const panel = node("section", "sv-sheet");
      const header = node("header", "sv-sheet-header");
      const heading = node("h2", "", title);
      // The X and the backdrop close the way Escape does, so whatever opened the sheet hears it.
      const closeSheet = () => shell.dispatchEvent(new CustomEvent("sv-close"));
      const close = button("Close", "sv-icon-button", closeSheet, "x");
      header.append(heading, close);
      const body = node("div", "sv-sheet-body");
      panel.append(header, body);
      shell.append(panel);
      shell.addEventListener("click", event => { if (event.target === shell) closeSheet(); });
      shell.addEventListener("sv-close", () => shell.dismiss());
      shell.focusLater(close);
      return { shell, panel, body, heading };
    }

    function accountSheet(title, parent = null) {
      const panel = sheet(title);
      panel.childSheets = new Set();
      parent?.childSheets.add(panel);
      panel.owner = viewerId();
      panel.controller = new AbortController();
      accountSheets.add(panel);
      const dismiss = panel.shell.dismiss;
      panel.shell.dismiss = () => {
        if (!accountSheets.delete(panel)) return;
        parent?.childSheets.delete(panel);
        for (const child of [...panel.childSheets]) child.shell.dismiss();
        if (archivePanel === panel) archivePanel = null;
        panel.controller.abort();
        dismiss();
      };
      panel.current = () => accountSheets.has(panel) && viewerId() === panel.owner && (!parent || parent.current());
      return panel;
    }

    // Tray ---------------------------------------------------------------------------------

    async function refreshTray() {
      const id = viewerId();
      if (!id || !hooks.capabilities?.()?.media_publication) {
        state.tray = [];
        hooks.onTrayChanged?.();
        return state.tray;
      }
      const request = ++state.trayRequest;
      try {
        const result = await hooks.api("/api/story-items/tray");
        if (request !== state.trayRequest || viewerId() !== id) return state.tray;
        state.tray = orderTray(Array.isArray(result?.items) ? result.items : []);
      } catch (_) {
        if (request !== state.trayRequest) return state.tray;
        state.tray = [];
      }
      state.trayViewer = id;
      hooks.onTrayChanged?.();
      return state.tray;
    }

    function trayEntries() {
      return state.trayViewer === viewerId() ? state.tray : [];
    }

    function hasLiveStory(userId) {
      return trayEntries().some(entry => entry.author?.id === userId);
    }

    function trayButton(entry) {
      const item = node("button", `people-row-item has-story${entry.has_unseen ? " is-unseen" : ""}${entry.close_friends ? " is-close-friends" : ""}`);
      item.type = "button";
      const face = avatar(entry.author, "people-row-avatar");
      item.append(face, node("span", "people-row-name", entry.is_own ? "Your story" : (entry.author.display_name || entry.author.handle)));
      item.setAttribute("aria-label", entry.is_own
        ? "Open your story"
        : `Story from ${entry.author.display_name || entry.author.handle}${entry.has_unseen ? ", new" : ""}`);
      item.addEventListener("click", () => openAuthor(entry.author.id, { queue: trayEntries().map(e => e.author.id) }));
      return item;
    }

    // The Home strip: one tile for you (a story being posted, your live story, or the plus), then everyone else.
    function renderTray(list) {
      const entries = trayEntries();
      const own = entries.find(entry => entry.is_own);
      list.append(state.posting ? postingButton(state.posting) : own ? ownStoryTile(own) : addStoryButton());
      for (const live of hooks.liveTrayItems?.() || []) list.append(live);
      for (const entry of entries) if (!entry.is_own) list.append(trayButton(entry));
    }

    function addStoryButton() {
      const item = node("button", "people-row-item is-you sv-add-story");
      item.type = "button";
      item.setAttribute("aria-label", "Add to your story");
      const face = avatar(hooks.viewer(), "people-row-avatar");
      const plus = node("span", "people-row-plus", "+");
      plus.setAttribute("aria-hidden", "true");
      face.append(plus);
      item.append(face, node("span", "people-row-name", "Your story"));
      item.addEventListener("click", () => openCreate());
      return item;
    }

    // Your own tile once a Story is live. Both doors stay open, the way Instagram keeps them: the
    // face opens what you posted, the plus on its corner starts the next one. Posting one Story
    // must never take away the way to post another, so the plus never leaves this row.
    function ownStoryTile(entry) {
      const item = node("div", `people-row-item is-you has-story${entry.close_friends ? " is-close-friends" : ""} sv-own-tile`);
      const open = node("button", "sv-own-open");
      open.type = "button";
      open.setAttribute("aria-label", "Open your story");
      open.addEventListener("click", () => openAuthor(entry.author.id, { queue: trayEntries().map(e => e.author.id) }));
      const face = node("span", "sv-own-face");
      const add = node("button", "people-row-plus sv-own-add");
      add.type = "button";
      add.setAttribute("aria-label", "Add to your story");
      const plus = node("span", "", "+");
      plus.setAttribute("aria-hidden", "true");
      add.append(plus);
      add.addEventListener("click", event => { event.stopPropagation(); openCreate(); });
      face.append(avatar(entry.author, "people-row-avatar"), add);
      item.append(open, face, node("span", "people-row-name", "Your story"));
      return item;
    }

    // Posting in the background --------------------------------------------------------------
    // Share closes the editor at once, the way Instagram does; the upload, the checks and the share
    // carry on here while the member keeps browsing, and the own tile in the tray shows how it goes.
    // A job keeps its upload and publication token between attempts. Only a matching
    // publication receipt establishes success; a refused repeat is not proof of absence.

    function postingButton(job) {
      const item = node("button", `people-row-item is-you sv-posting${job.failed ? " is-failed" : ""}`);
      item.type = "button";
      item.append(avatar(hooks.viewer(), "people-row-avatar"), node("span", "people-row-name", job.stopped ? "Check removal" : job.failed ? "Try again" : "Posting…"));
      item.setAttribute("aria-label", job.stopped ? `Story removal is unconfirmed. ${job.error}`
        : job.failed ? `${job.uncertain ? "Story sharing is unconfirmed" : "Your story was not shared"}. ${job.error}` : `Your story is posting. ${job.stage}`);
      item.addEventListener("click", () => openPostingChoices(job));
      return item;
    }

    function openPostingChoices(job) {
      if (state.posting !== job || !postingAccount(job)) return;
      job.sheet?.dismiss();
      const panel = accountSheet(job.stopped ? "Story removal is unconfirmed" : job.failed ? job.uncertain ? "Story sharing is unconfirmed" : "Your story wasn't shared" : "Your story is posting");
      panel.body.append(node("p", "sv-hint", job.failed ? job.error : job.stage));
      panel.body.append(node("p", "sv-hint", `${job.draft.file.name || "Story"} · ${job.draft.audience === "close_friends" ? "Close Friends" : "Your story"}`));
      if (job.draft.caption) panel.body.append(node("p", "sv-hint", job.draft.caption));
      // This sheet can stay open while the job finishes or the account changes; then it acts for nobody.
      const choice = (label, className, action, icon, text) => {
        panel.body.append(button(label, className, () => {
          if (!panel.current() || !postingAccount(job) || state.posting !== job) return;
          panel.shell.dismiss();
          action();
        }, icon, text));
      };
      state.postingSheet = panel.shell;
      job.sheet = panel.shell;
      const dismiss = panel.shell.dismiss;
      panel.shell.dismiss = () => {
        if (job.sheet === panel.shell) job.sheet = null;
        if (state.postingSheet === panel.shell) state.postingSheet = null;
        dismiss();
      };
      if (job.storageProblem) choice("Recover unfinished Story", "sv-choice", () => openRecovery(), "refresh", "Recover unfinished Story");
      if (job.stopped) {
        choice("Check Story removal", "sv-choice", () => void checkRemoval(job), "refresh", "Check removal");
        choice("Forget local recovery copy", "sv-choice sv-danger", () => void forgetRecovery(job), "trash", "Forget local copy");
        return;
      }
      if (job.failed && job.retry) choice("Try again", "sv-choice", () => void runPosting(job), "refresh", "Try again");
      // The server turned the story down (its text, most often): change it and share the same upload.
      if (job.failed && !job.retry && job.phase === "share") {
        choice("Edit this story", "sv-choice", () => openComposer(job.draft.file, job), "edit", "Edit story");
      }
      if (job.failed && !job.retry && job.phase === "upload") {
        choice("Choose another photo or video", "sv-choice", () => { stopPosting(job); pickFile(); }, "image", "Choose another");
      }
      choice(job.failed ? "Discard this story" : "Stop posting this story", "sv-choice sv-danger",
        () => stopPosting(job), "trash", job.failed ? "Discard" : "Stop posting");
    }

    function watchLeave(active, reason = "posting") {
      if (active) leaveReasons.add(reason); else leaveReasons.delete(reason);
      if (!win?.addEventListener) return;
      if (leaveReasons.size && !leaveGuard) {
        leaveGuard = event => { event.preventDefault(); event.returnValue = ""; };
        win.addEventListener("beforeunload", leaveGuard);
      } else if (!leaveReasons.size && leaveGuard) {
        win.removeEventListener("beforeunload", leaveGuard);
        leaveGuard = null;
      }
    }

    function postingJob(draft) {
      return {
        draft, owner: viewerId(), stage: "Uploading…", phase: "upload", failed: false, retry: false, error: "",
        cancelled: false, stopped: false, sharing: false, shared: false, uncertain: false, mediaId: null,
        epoch: postingEpoch, running: false, uploadKey: hooks.uuid(), uploadId: null, publishToken: hooks.uuid(), controller: new AbortController(),
        uploadStarted: false, journalRevision: null,
      };
    }

    function startPosting(draft, saved = null) {
      const job = saved || postingJob(draft);
      job.draft = draft;
      job.action = "publish";
      state.posting = job;
      void runPosting(job);
      return job;
    }

    function journal(job, remove = false) {
      const value = remove ? null : {
        version: RECOVERY_VERSION, revision: hooks.uuid(), savedAt: Date.now(), owner: job.owner,
        publishToken: job.publishToken, uploadKey: job.uploadKey, uploadId: job.uploadId,
        mediaId: job.mediaId, uploadStarted: Boolean(job.uploadStarted), shared: Boolean(job.shared),
        action: job.stopped ? "remove" : job.action === "draft" ? "draft" : "publish",
        draft: { ...job.draft, elements: JSON.parse(JSON.stringify(job.draft.elements)) },
      };
      // Serialize a stop/clear behind any already queued save. Signing out waits for
      // these writes, then removes local copies; account changes never revive a job.
      const work = Promise.allSettled([job.journalWork, recoveryReset]).then(async () => {
        if (!postingAccount(job)) throw recoveryError("The Story account changed. Nothing new was sent.");
        const revision = await recovery.compare(job.owner, job.publishToken, job.journalRevision ?? null, value, () => postingAccount(job));
        job.journalRevision = revision;
        job.storageProblem = false;
      });
      job.journalWork = work;
      recoveryWrites.add(work);
      void work.then(() => recoveryWrites.delete(work), () => recoveryWrites.delete(work));
      return work;
    }

    async function clearRecovery(job) {
      try { await journal(job, true); return true; }
      catch (error) {
        job.storageProblem = true;
        if (postingAccount(job)) say("The local recovery copy could not be cleared. You can check it through Recover unfinished Story.", true);
        return false;
      }
    }

    function openRecovery({ parent = null } = {}) {
      const panel = accountSheet("Recover unfinished Story", parent);
      const status = node("p", "sv-hint", "Loading Stories kept on this device…");
      status.setAttribute("role", "status");
      const entries = node("div", "sv-recovery-list");
      const reload = button("Reload Story recovery copies", "sv-choice", () => void load(), "refresh", "Reload");
      panel.body.append(status, entries, reload);
      let busy = false, generation = 0;
      const previews = new Set(), controls = new Set();
      const dismiss = panel.shell.dismiss;
      function clearEntries() {
        generation += 1;
        entries.replaceChildren(); controls.clear();
        for (const url of previews) URL.revokeObjectURL(url);
        previews.clear();
      }
      panel.shell.dismiss = () => { clearEntries(); dismiss(); };
      function setBusy(value) {
        busy = value; reload.disabled = value;
        for (const control of controls) control.disabled = value;
      }
      async function readRecords() {
        await recoveryReset;
        const records = await recovery.list(panel.owner);
        if (!Array.isArray(records) || records.some(row => !validRecovery(row, panel.owner))) throw recoveryError("A Story recovery copy is unreadable. It has been preserved.");
        return records.sort((a, b) => b.savedAt - a.savedAt);
      }
      const canRecover = () => !state.composer && (!state.posting || (state.posting.storageProblem
        && !state.posting.running && !state.posting.removing && !state.posting.forgetting));
      async function load({ focusIndex = null } = {}) {
        if (!panel.current() || busy) return;
        clearEntries(); setBusy(true);
        const render = generation;
        const current = () => panel.current() && generation === render;
        try {
          const records = await readRecords();
          if (!current()) return;
          status.textContent = records.length ? `${records.length} of ${RECOVERY_LIMIT} local copies. ${parent ? "You can delete another unpublished draft to make room. Your open editor stays here." : "Choose one to review before continuing."} Copies stay in this browser until completed, deleted, forgotten or signed out.`
            : "No saved drafts or unfinished Stories are kept in this browser.";
          const reviewButtons = [];
          for (const [index, row] of records.entries()) {
            const name = row.draft.file.name || "Story";
            const kind = row.action === "draft" ? "Unpublished draft" : row.action === "remove" ? "Removal unfinished" : row.shared ? "Sharing needs checking" : "Sharing unfinished";
            const audience = row.action === "draft" ? "" : row.draft.audience === "close_friends" ? " · Close Friends" : " · Your story";
            const date = new Date(row.savedAt);
            const saved = Number.isFinite(date.getTime()) ? date.toLocaleString() : "Time unavailable";
            const caption = row.draft.caption.trim() || row.draft.elements.find(item => item.type === "text" && item.text)?.text || "No caption";
            const label = `${name}, ${kind}${audience}`;
            const card = node("article", "sv-recovery-card");
            const review = button(`Review unfinished Story ${index + 1}: ${label}`, "sv-choice sv-recovery-open", async () => {
              if (!current() || busy) return;
              if (!canRecover()) {
                say("Finish or stop the current Story before recovering another.", true); return;
              }
              const previous = state.posting;
              setBusy(true);
              try {
                if (previous && previous.publishToken !== row.publishToken) {
                  if (!(await hooks.confirm?.("Leave the current Story and open this recovery copy? Any work that could not be saved will be lost.", { confirm: "Open recovery copy", destructive: true }))) return;
                }
                if (!current() || !canRecover() || state.posting !== previous) return;
                const latest = (await readRecords()).find(record => record.publishToken === row.publishToken);
                if (!current() || !canRecover() || state.posting !== previous) return;
                if (!latest || latest.revision !== row.revision) {
                  status.textContent = "This copy changed or was removed in another editor. Reload to see the current copies."; return;
                }
                if (state.posting) cancelPosting(state.posting);
                const job = { ...latest, journalRevision: latest.revision, epoch: postingEpoch,
                  phase: row.mediaId ? "share" : "upload", stage: "Waiting for you", failed: true, retry: true,
                  error: row.action === "remove" ? "Removal is not confirmed. Check its status before forgetting the local copy."
                    : row.shared ? "Sharing may already have completed. Try again checks the same publication."
                      : "Your file and edits are kept here. Try again resumes this Story.",
                  uncertain: row.shared, stopped: row.action === "remove", cancelled: false, running: false, sharing: false,
                  controller: new AbortController(),
                };
                if (row.action === "draft") { panel.shell.dismiss(); openComposer(latest.draft.file, null, job); return; }
                state.posting = job; panel.shell.dismiss(); hooks.onTrayChanged?.(); openPostingChoices(job);
              } catch (error) { if (current()) status.textContent = error?.message || "This copy could not be opened. Your current Story is still here."; }
              finally { if (current()) setBusy(false); }
            }, "", "");
            review.textContent = "";
            const preview = node("span", "sv-recovery-preview");
            preview.setAttribute("aria-hidden", "true");
            preview.append(svg(row.draft.file.type.startsWith("video/") ? "video" : "image"));
            if (row.draft.file.type.startsWith("image/")) {
              try {
                const url = URL.createObjectURL(row.draft.file), image = node("img");
                previews.add(url); image.alt = ""; image.src = url;
                image.addEventListener("error", () => {
                  if (!current() || !previews.has(url)) return;
                  image.remove(); URL.revokeObjectURL(url); previews.delete(url);
                });
                preview.append(image);
              } catch (_) { /* File name and caption remain usable when no thumbnail can be decoded. */ }
            }
            const description = node("span", "sv-recovery-description");
            const captionNode = node("span", "sv-recovery-caption", String(caption));
            const detail = node("span", "sv-recovery-detail", `${kind}${audience} · ${row.draft.file.type.startsWith("video/") ? "Video" : "Photo"} · Saved ${saved}`);
            captionNode.id = `story-draft-caption-${hooks.uuid()}`; detail.id = `story-draft-detail-${hooks.uuid()}`;
            review.setAttribute("aria-describedby", `${captionNode.id} ${detail.id}`);
            description.append(node("strong", "sv-recovery-name", name), captionNode, detail);
            review.append(preview, description); controls.add(review); reviewButtons.push(review); card.append(review);
            if (row.action === "draft") {
              if (state.composer?.ownsDraft(row.publishToken)) card.append(node("p", "sv-hint", "Open in this editor. Use its Save draft or Discard controls."));
              else {
                const remove = button(`Delete local Story draft ${index + 1}: ${name}`, "sv-choice sv-danger sv-recovery-delete", async () => {
                  if (!current() || busy) return;
                  if (state.composer?.ownsDraft(row.publishToken)) { status.textContent = "This draft is open. Close the list and use the editor's controls."; return; }
                  setBusy(true);
                  let deleted = false;
                  try {
                    if (!(await hooks.confirm?.(`Delete the local draft “${name}”? This cannot be undone. Published Stories are unchanged.`, { confirm: "Delete draft", destructive: true }))) return;
                    if (!current() || state.composer?.ownsDraft(row.publishToken)) return;
                    const job = { ...row, journalRevision: row.revision, epoch: postingEpoch };
                    await journal(job, true);
                    deleted = true;
                  } catch (error) { if (current()) status.textContent = error?.message || "The draft could not be deleted. Reload before trying again."; }
                  finally { if (current()) setBusy(false); }
                  if (deleted && current()) { await load({ focusIndex: index }); if (panel.current()) say("Local draft deleted. Your open editor and published Stories are unchanged."); }
                }, "trash", "Delete draft");
                controls.add(remove); card.append(remove);
              }
            }
            entries.append(card);
          }
          if (focusIndex !== null) panel.shell.focusLater(reviewButtons[Math.min(focusIndex, reviewButtons.length - 1)] || reload);
        } catch (error) { if (current()) status.textContent = error?.message || "Recovery copies could not be read. Try again."; }
        finally { if (current()) setBusy(false); }
      }
      void load();
    }

    async function forgetRecovery(job) {
      if (job.forgetting || job.running || job.removing) return;
      job.forgetting = true;
      try {
        const confirmed = await hooks.confirm?.("Forget this local recovery copy? This does not remove a Story that may already be published. Check your profile and archive first.", { confirm: "Forget local copy", destructive: true });
        if (!confirmed || !postingAccount(job) || state.posting !== job) return;
        if (await clearRecovery(job) && postingAccount(job) && state.posting === job) {
          cancelPosting(job); say("Local recovery copy forgotten. Published Stories are unchanged.");
        }
      } catch (_) { if (postingAccount(job)) say("The local copy could not be forgotten. Try again.", true); }
      finally { job.forgetting = false; }
    }

    async function checkRemoval(job) {
      if (!postingAccount(job) || job.removing || job.running || job.forgetting) return;
      job.running = true;
      try { await journal(job); if (postingAccount(job)) await unshare(job, null); }
      catch (error) {
        if (error?.storyStorage) job.storageProblem = true;
        if (postingAccount(job)) say(error?.message || "Removal could not be checked.", true);
      }
      finally { job.running = false; }
    }

    // Recover the owner-scoped publication, including expired and removed Stories.
    async function sharedStory(job) {
      if (!job.mediaId || !postingAccount(job)) return null;
      if (job.publishToken) {
        try {
          const result = await hooks.api(`/api/story-publications/${encodeURIComponent(job.publishToken)}`);
          if (!postingAccount(job)) return null;
          if (!publicationReceipt(job, result)) throw new Error("The publication receipt could not be confirmed.");
          return result;
        } catch (error) { if (error?.status === 404) return null; throw error; }
      }
      const result = await hooks.api(`/api/story-items/by-author/${encodeURIComponent(job.owner)}`);
      if (!postingAccount(job)) return null;
      if (!Array.isArray(result?.items)) throw new Error("The Story lookup could not be confirmed.");
      return result.items.find(item => ownedStory(job, item)) || null;
    }

    const ownedStory = (job, item) => typeof item?.id === "string" && Boolean(item.id) && item.is_own === true
      && item.author?.id === job.owner && item.media?.url === `/api/media/${job.mediaId}`;
    const publicationReceipt = (job, item) => typeof item?.id === "string" && Boolean(item.id)
      && item.publication?.token === job.publishToken && typeof item.publication.replayed === "boolean"
      && ["live", "archived", "removed", "unavailable"].includes(item.publication.state);

    async function runPosting(job) {
      if (state.posting !== job || job.cancelled || job.running || !postingAccount(job)) return;
      job.running = true;
      const current = () => state.posting === job && !job.cancelled && postingAccount(job);
      const changed = () => { if (current()) hooks.onTrayChanged?.(); };
      Object.assign(job, { failed: false, retry: false, error: "", phase: job.mediaId ? "share" : "upload" });
      job.stage = job.mediaId ? "Sharing…" : "Uploading…";
      watchLeave(true);
      changed();
      try {
        await journal(job);
        if (!current()) return;
        if (!job.mediaId) {
          const mediaId = await uploadForStory(job, text => { job.stage = text; changed(); });
          if (!mediaId) return;
          job.mediaId = mediaId;
          if (!current()) return;
        }
        Object.assign(job, { phase: "share", stage: "Sharing…" });
        changed();
        let item;
        const previousUnknown = job.uncertain;
        job.shared = true;
        // A restored copy must conservatively assume dispatch may have happened.
        await journal(job);
        if (!current()) return;
        job.sharing = true;
        try {
          item = await hooks.api("/api/story-items", {
            method: "POST", csrf: true,
            json: {
              media_asset_id: job.mediaId,
              publish_token: job.publishToken,
              audience: job.draft.audience,
              caption: job.draft.caption.trim(),
              elements: stickerPayload(job.draft.elements),
            },
          });
        } catch (error) {
          // A lost answer may hide a committed publication; read its retained receipt first.
          const unsure = previousUnknown || !error?.status || error.status >= 500 || error.status === 408;
          job.uncertain = unsure;
          if (!unsure) job.shared = false;
          if (!postingAccount(job)) return;
          item = unsure ? await sharedStory(job).catch(() => null) : null;
          if (!item) throw error;
        } finally {
          job.sharing = false;
        }
        if (!postingAccount(job)) return;
        const receipt = publicationReceipt(job, item);
        const unavailable = receipt && ["removed", "unavailable"].includes(item.publication.state);
        if (!receipt || (!unavailable && (!ownedStory(job, item) || item.audience !== job.draft.audience))) {
          job.uncertain = true;
          throw new Error("The Story publication could not be confirmed.");
        }
        job.uncertain = false;
        if (job.stopped) { await unshare(job, item); return; }
        if (!current()) return;
        await clearRecovery(job);
        if (!current()) return;
        state.posting = null;
        job.sheet?.dismiss();
        watchLeave(false);
        say(item.publication.state === "removed" ? "This Story was already published and removed. It was not posted again."
          : item.publication.state === "unavailable" ? "This Story was already published but is no longer available. It was not posted again."
          : item.publication.state === "archived" ? "This Story was already published and is now in your archive."
          : job.draft.audience === "close_friends" ? "Shared with Close Friends" : "Shared to your story");
        await refreshTray();
      } catch (error) {
        if (!postingAccount(job)) return;
        if (error?.storyStorage) job.storageProblem = true;
        if (job.stopped) {
          if (job.shared) await unshare(job, null);
          else {
            await clearRecovery(job);
            if (postingAccount(job) && job.phase === "share") say("Stopped. Your story was not posted.");
          }
          return;
        }
        if (!current()) return;
        const status = error?.status;
        const retry = job.uncertain || (error?.retry ?? (!status || status >= 500 || status === 408 || status === 429));
        let message = error?.message || "The story could not be shared.";
        if (job.phase === "share" && job.uncertain) message = "Story sharing is unconfirmed. Check your profile or retry the same Story.";
        Object.assign(job, { failed: true, retry, error: message });
        if (!error?.storyStorage) {
          try { await journal(job); }
          catch (_) { job.storageProblem = true; message += " The latest state could not be saved on this device."; job.error = message; }
          if (!current()) return;
        }
        watchLeave(Boolean(job.storageProblem));
        say(`${message} Tap your story to ${retry ? "try again" : "see what to do"}.`, true);
        changed();
      } finally { job.running = false; }
    }

    // "Stop posting" once the share was sent: the story may exist by now, so it is taken down again.
    // Every outcome is said: taken down, never posted, or not known (the member is asked to look).
    async function unshare(job, item) {
      if (!postingAccount(job) || job.removing) return;
      job.removing = true;
      let found = ownedStory(job, item) ? item : null;
      let receipt = publicationReceipt(job, item) ? item : null;
      if (!found) {
        try {
          receipt = receipt || await sharedStory(job);
          found = ownedStory(job, receipt) ? receipt : null;
        } catch (_) { /* A missing receipt while a write is unresolved does not prove absence. */ }
      }
      if (!postingAccount(job)) { job.removing = false; return; }
      if (receipt && publicationReceipt(job, receipt) && ["removed", "unavailable"].includes(receipt.publication.state)) {
        await clearRecovery(job);
        if (!postingAccount(job)) { job.removing = false; return; }
        if (state.posting === job) cancelPosting(job);
        job.removing = false;
        say(receipt.publication.state === "removed" ? "This Story had already been removed. It was not posted again."
          : "The previous Story publication is no longer available. Check your archive.");
        return;
      }
      if (found) {
        try {
          const result = await hooks.api(`/api/story-items/${encodeURIComponent(found.id)}`, { method: "DELETE", csrf: true });
          if (!postingAccount(job)) { job.removing = false; return; }
          if (result?.removed !== true) throw new Error("The deletion could not be confirmed.");
          await clearRecovery(job);
          if (!postingAccount(job)) { job.removing = false; return; }
          if (state.posting === job) cancelPosting(job);
          say("Your story was taken down. It may have been seen before removal.");
        } catch (error) {
          if (!postingAccount(job)) { job.removing = false; return; }
          say(error?.status === 404 ? "This Story is no longer available. Its deletion was not confirmed."
            : "The Story removal is unconfirmed. Check your profile and archive.", true);
        }
      } else {
        say("Your story may have been shared. Its publication could not be confirmed. Check your profile and archive.", true);
      }
      job.removing = false;
      if (postingAccount(job)) await refreshTray();
    }

    async function stopPosting(job) {
      const failed = job.failed;
      job.stopped = true;
      cancelPosting(job);
      try {
        if (job.shared) await journal(job);
        else await clearRecovery(job);
      } catch (_) {
        if (postingAccount(job)) say("The stop could not be saved on this device. Check the recovery copy before continuing it.", true);
      }
      if (!postingAccount(job)) return;
      if (job.sharing) return;  // runPosting takes the story down when the answer arrives.
      if (job.shared) void unshare(job, null);
      else say(failed ? "Discarded. Your story was not posted." : "Stopped. Your story was not posted.");
    }

    function cancelPosting(job) {
      job.cancelled = true;
      job.controller?.abort();
      job.sheet?.dismiss();
      if (state.posting === job) {
        state.posting = null;
        watchLeave(false);
        hooks.onTrayChanged?.();
      }
    }

    // Upload -------------------------------------------------------------------------------

    // Errors say whether the same file could work on another try (a timeout, a dropped connection) or not.
    const uploadError = (message, retry) => Object.assign(new Error(message), { retry });

    async function uploadForStory(job, progress) {
      const viewer = hooks.viewer();
      const csrf = hooks.csrf?.();
      if (!viewer || !csrf || !postingAccount(job)) throw uploadError("Sign in again before posting.", false);
      const active = () => postingAccount(job) && !job.cancelled;
      let payload;
      if (!job.uploadId && job.uploadStarted) {
        try { payload = await hooks.api(`/api/uploads/by-submission/${encodeURIComponent(job.uploadKey)}`, { signal: job.controller.signal }); }
        catch (error) { if (error?.status !== 404) throw error; }
        if (!active()) return null;
      }
      if (job.uploadId) {
        payload = await hooks.api(`/api/uploads/${encodeURIComponent(job.uploadId)}`, { signal: job.controller.signal });
      } else if (!payload) {
        const body = new FormData();
        body.append("file", job.draft.file);
        body.append("submission_key", job.uploadKey);
        body.append("purpose", "post");
        progress("Uploading…");
        job.uploadStarted = true;
        await journal(job);
        if (!active()) return null;
        const response = await fetch("/api/uploads", {
          method: "POST", body, credentials: "same-origin", signal: job.controller.signal,
          headers: { "X-CSRF-Token": csrf, "X-Account-ID": job.owner },
        });
        try { payload = await response.json(); } catch (_) { payload = {}; }
        if (!response.ok) {
          const detail = typeof payload?.detail === "string" ? payload.detail : payload?.detail?.message;
          const receiving = response.status === 409 && payload?.detail?.code === "receiving_in_progress";
          throw uploadError(detail || "The upload could not start. Try again.", receiving || response.status >= 500 || response.status === 408 || response.status === 429);
        }
      }
      const started = Date.now();
      let current = payload;
      while (true) {
        if (!postingAccount(job)) return null;
        if (typeof current?.id !== "string" || !current.id || (job.uploadId && current.id !== job.uploadId)) throw uploadError("The upload could not be confirmed. Retry the same file.", true);
        job.uploadId = current.id;
        if (!active()) {
          if (job.stopped) void hooks.api(`/api/uploads/${encodeURIComponent(job.uploadId)}`, { method: "DELETE", csrf: true }).catch(() => {});
          return null;
        }
        if (current.state === "succeeded") {
          if (current.ready !== true) throw uploadError("This media is not available for Story publication.", false);
          return current.id;
        }
        if (current?.state === "rejected") throw uploadError("This media was not approved.", false);
        if (current?.state === "failed") throw uploadError("This file could not be processed. Try another photo or video.", false);
        if (["cancelled", "expired"].includes(current?.state)) {
          job.uploadId = null; job.uploadStarted = false; job.uploadKey = hooks.uuid();
          throw uploadError("The upload stopped before it finished. Try again.", true);
        }
        if (current?.state === "awaiting_review") throw uploadError("This media needs a safety review before it can be posted.", false);
        if (!["receiving", "queued", "processing", "retry_wait"].includes(current?.state)) throw uploadError("The upload status could not be confirmed.", true);
        if (Date.now() - started > UPLOAD_WAIT_MS) throw uploadError("Processing is taking longer than usual. Try again soon.", true);
        progress(current?.state === "processing" ? "Checking your media…" : "Getting it ready…");
        await new Promise(resolve => {
          const finish = () => { draftClock.clearTimeout(timer); job.controller.signal.removeEventListener("abort", finish); resolve(); };
          const timer = draftClock.setTimeout(finish, UPLOAD_POLL_MS);
          job.controller.signal.addEventListener("abort", finish, { once: true });
        });
        if (!active()) return null;
        current = await hooks.api(`/api/uploads/${encodeURIComponent(job.uploadId)}`, { signal: job.controller.signal });
      }
    }

    // Composer -----------------------------------------------------------------------------

    // Offer the same explicit capture choices on touchscreens and computers. Opening the
    // choices does not request camera access; only the chosen photo/video action does.
    function openCreate({ camera = false, mode = "photo" } = {}) {
      if (!viewerId() || (hooks.verified && !hooks.verified())) { hooks.signIn?.(); return; }
      if (state.posting) { openPostingChoices(state.posting); return; }
      if (state.composer) { state.composer.shell.focus(); return; }
      if (!canWrite()) { say("Posting is paused right now. You can still review recovery copies.", true); openRecovery(); return; }
      const owner = viewerId(), request = ++createRequest;
      createOwner = owner;
      filePicker?.remove(); filePicker = null;
      const current = () => owner === viewerId() && request === createRequest;
      if (camera) {
        if (hooks.openCamera && hooks.cameraAvailable?.(mode) !== false) {
          hooks.openCamera({
            title: "Story camera", mode, maxVideoSeconds: 60, useLabel: "Next",
            onCapture: file => { if (current()) { createRequest += 1; openComposer(file); } },
            onFallback: () => { if (current()) pickFile(); },
          });
        } else pickFile(mode);
        return;
      }
      const panel = accountSheet("Add to your story");
      const capture = mode => { if (!panel.current() || !current()) return; panel.shell.dismiss(); openCreate({ camera: true, mode }); };
      panel.body.append(
        button("Take photo", "sv-choice", () => capture("photo"), "image", "Take photo"),
        button("Record video", "sv-choice", () => capture("video"), "video", "Record video"),
        button("Upload photo or video", "sv-choice", () => { if (!panel.current() || !current()) return; panel.shell.dismiss(); pickFile(); }, "upload", "Upload photo or video"),
        button("Recover unfinished Story", "sv-choice", () => { if (!panel.current() || !current()) return; panel.shell.dismiss(); openRecovery(); }, "refresh", "Recover unfinished Story"),
        node("p", "sv-hint", "Photos, videos and applied edits save on this device. Finish each edit with Done and check the save status before leaving. Drafts stay in this browser until removed or signed out."),
      );
    }

    function pickFile(mode = "") {
      if (!viewerId()) return;
      const owner = viewerId(), request = ++createRequest;
      createOwner = owner;
      filePicker?.remove();
      const input = node("input");
      filePicker = input;
      const remove = () => { input.remove(); if (filePicker === input) filePicker = null; };
      input.type = "file";
      input.accept = mode === "photo" ? "image/*" : mode === "video" ? "video/*" : "image/*,video/*";
      if (mode) input.setAttribute("capture", "environment");
      input.hidden = true;
      input.addEventListener("cancel", () => { if (request === createRequest) createRequest += 1; remove(); });
      input.addEventListener("change", () => {
        const file = input.files?.[0];
        remove();
        if (file && owner === viewerId() && request === createRequest) { createRequest += 1; openComposer(file); }
      });
      doc.body.append(input);
      input.click();
    }

    // A processed video has no thumbnail, so its own first frame stands in for one.
    function cover(media) {
      const url = media?.thumbnail_url || media?.url;
      if (!url) return null;
      if (media.type === "video" && !media.thumbnail_url) {
        const film = node("video");
        film.src = `${url}#t=0.1`;
        film.muted = true;
        film.preload = "metadata";
        film.setAttribute("playsinline", "");
        film.setAttribute("aria-hidden", "true");
        return film;
      }
      const image = node("img");
      image.src = url;
      image.alt = "";
      image.loading = "lazy";
      return image;
    }

    function backdrop(url) {
      const image = node("img", "sv-backdrop");
      image.src = url;
      image.alt = "";
      image.setAttribute("aria-hidden", "true");
      return image;
    }

    // Fill the stage when the media has about its shape; otherwise show all of it (the stage then shows a
    // blurred copy of a photo, or a dark gradient behind a video).
    function applyFit(stage, width, height) {
      const box = stage.getBoundingClientRect?.();
      const ratio = box && box.width > 0 && box.height > 0 ? box.width / box.height : 9 / 16;
      stage.classList.toggle("is-contained", !fitsStage(width, height, ratio));
    }

    function watchFit(stage, media, width = null, height = null, current = () => true) {
      if (width > 0 && height > 0) applyFit(stage, width, height);
      media.addEventListener?.(media.tagName === "VIDEO" ? "loadedmetadata" : "load", () => {
        if (!current()) return;
        applyFit(stage, media.naturalWidth || media.videoWidth || width, media.naturalHeight || media.videoHeight || height);
      });
    }

    // A failed job can be reopened here (`resume`): its text and stickers come back, and sharing it again
    // reuses the upload it already has.
    function openComposer(file, resume = null, savedDraft = null) {
      if (!viewerId() || (resume && (resume.owner !== viewerId() || state.posting !== resume))) return;
      if (savedDraft && (!postingAccount(savedDraft) || savedDraft.action !== "draft" || !validRecovery(savedDraft, viewerId()))) return;
      if (!resume && state.posting) { openPostingChoices(state.posting); return; }
      if (state.composer) { state.composer.shell.focus(); say("Finish or discard your open Story first.", true); return; }
      const problem = storyFileProblem(file);
      if (problem) { say(problem, true); return; }
      const saved = resume?.draft || savedDraft?.draft;
      let draftRecord = savedDraft;
      const draft = {
        file, elements: (saved?.elements || []).map(element => ({ ...element })), caption: saved?.caption || "",
        selected: -1, busy: false, closed: false, problem: "",
      };
      const draftSignature = () => JSON.stringify([draft.caption, draft.elements]);
      let savedSignature = savedDraft ? draftSignature() : null;
      let autosaveTimer = null, autosaveWork = null, autosaveStopped = false, autosaveError = false;
      const shell = overlay("sv-composer", "Create a story");
      const dismissShell = shell.dismiss;
      const composer = { shell, draft, owner: viewerId(), childSheets: new Set(), finish,
        ownsDraft: token => draftRecord?.publishToken === token,
        alive: () => !draft.closed && state.composer === composer && viewerId() === composer.owner,
        current: () => composer.alive() && !draft.busy };
      state.composer = composer;
      watchLeave(true, composer);
      shell.dismiss = finish;
      let stopDrag = () => {}, confirming = false;
      const stage = node("div", "sv-stage");
      const url = URL.createObjectURL(file);
      const isVideo = file.type.startsWith("video/");
      const media = isVideo ? node("video", "sv-media") : node("img", "sv-media");
      if (!isVideo) stage.append(backdrop(url));
      media.src = url;
      if (isVideo) { media.muted = true; media.loop = true; media.autoplay = true; media.setAttribute("playsinline", ""); }
      else media.alt = "";
      const layer = node("div", "sv-layer");
      stage.append(media, layer);
      watchFit(stage, media, null, null, composer.current);
      if (isVideo) {
        media.addEventListener?.("loadedmetadata", () => {
          const tooLong = storyFileProblem(file, { durationMs: Number(media.duration) * 1000, maxBytes: Infinity });
          if (!tooLong || !composer.alive()) return;
          draft.problem = tooLong;
          status.textContent = tooLong;
          status.classList.add("is-error");
          for (const choice of shareRow.children) choice.disabled = true;
        });
      }

      const top = node("div", "sv-composer-top");
      // Closing a reopened story keeps it waiting in the tray as it was.
      const close = button(resume ? "Close without sharing" : savedDraft ? "Close story editor" : "Discard this story", "sv-round", async () => {
        if (!composer.current() || confirming) return;
        if (savedDraft) { await saveDraft(); return; }
        confirming = true;
        close.disabled = true;
        try {
          if (!resume && (draftRecord || draft.elements.length || draft.caption.trim())
            && !(await hooks.confirm?.("Discard this story?", { confirm: "Discard", destructive: true }))) return;
          if (!composer.current()) return;
          stopAutosave(); saving(true);
          if (!resume && draftRecord && !(await clearRecovery(draftRecord))) {
            autosaveStopped = false; autosaveError = true; return;
          }
          if (composer.alive()) finish();
        } catch (error) {
          if (composer.alive()) { autosaveStopped = false; autosaveError = true; say(error?.message || "The discard could not be confirmed. Try again.", true); }
        } finally { confirming = false; saving(false); }
      }, "x");
      const tools = node("div", "sv-tools");
      const addTextTool = button("Add text", "sv-round sv-tool-text", () => editText(), "", "Aa");
      tools.append(
        addTextTool,
        button("Add a sticker", "sv-round", () => openStickers(), "smile"),
        button("Write a caption", "sv-round", () => editCaption(), "edit"),
        button("More story options", "sv-round", () => openMore(), "more"),
      );
      top.append(close, tools);

      const edit = node("div", "sv-edit-bar");
      edit.hidden = true;
      const smaller = button("Make it smaller", "sv-round", () => resize(-0.15), "", "−");
      const bigger = button("Make it bigger", "sv-round", () => resize(0.15), "", "+");
      const remove = button("Remove sticker", "sv-round sv-danger", () => removeSelected(), "trash");
      const editSelected = button("Edit selected text", "sv-round", () => {
        if (composer.current() && draft.elements[draft.selected]?.type === "text") editText(draft.selected);
      }, "edit");
      edit.append(smaller, bigger, editSelected, remove);

      // Two equal buttons, as on Instagram: each one shares at once, to everyone or to Close Friends.
      const bottom = node("div", "sv-composer-bottom");
      const status = node("p", "sv-status");
      status.textContent = resume ? "Share again to apply your changes." : "Finish each text or sticker edit with Done to include it in your draft.";
      status.setAttribute("role", "status");
      status.setAttribute("aria-live", "polite");
      const instructions = node("p", "sr-only", "Use arrow keys to move this sticker. Hold Shift for larger steps. Use the size buttons to resize it, or Delete to remove it. For text, use Edit selected text or F2.");
      instructions.id = `story-edit-help-${hooks.uuid()}`;
      const shareRow = node("div", "sv-share-row");
      const toStory = node("button", "publish-button sv-share sv-share-story");
      toStory.type = "button";
      toStory.setAttribute("aria-label", "Share to your story");
      toStory.append(avatar(hooks.viewer(), "sv-share-avatar"), node("span", "", "Your story"));
      toStory.addEventListener("click", () => publish("public"));
      const toFriends = node("button", "sv-share sv-share-friends");
      toFriends.type = "button";
      toFriends.setAttribute("aria-label", "Share with Close Friends");
      toFriends.append(svg("users"), node("span", "", "Close Friends"));
      toFriends.addEventListener("click", () => publish("close_friends"));
      shareRow.append(toStory, toFriends);
      const saveDraftButton = button("Save story draft", "sv-draft-save", () => void saveDraft(), "", "Save draft");
      saveDraftButton.hidden = Boolean(resume);
      const draftStatus = node("p", "sv-status sv-draft-status");
      draftStatus.setAttribute("role", "status"); draftStatus.setAttribute("aria-live", "polite");
      draftStatus.hidden = Boolean(resume);
      bottom.append(instructions, status, draftStatus, saveDraftButton, shareRow);

      shell.append(stage, top, edit, bottom);
      shell.addEventListener("sv-close", () => close.click());
      if (draft.elements.length) paint();
      close.focus();

      function editCaption() {
        if (!composer.current()) return;
        const editor = accountSheet("Caption", composer);
        const input = node("textarea", "sv-text-input");
        input.maxLength = 200;
        input.rows = 3;
        input.value = draft.caption;
        input.placeholder = "Say something about this story";
        input.setAttribute("aria-label", "Caption");
        const done = node("button", "publish-button", "Done");
        done.type = "button";
        done.addEventListener("click", () => { if (!editor.current()) return; draft.caption = input.value.trim(); queueAutosave(); editor.shell.dismiss(); });
        editor.body.append(input, node("p", "sv-hint", "Shown under your story. Up to 200 characters."), done);
        editor.shell.focusLater(input);
      }

      function openMore() {
        if (!composer.current()) return;
        const menu = accountSheet("Story options", composer);
        menu.body.append(
          button("Manage saved Story drafts", "sv-choice", () => { if (!menu.current()) return; menu.shell.dismiss(); openRecovery({ parent: composer }); }, "layers", "Drafts and unfinished Stories"),
          button("Open your story archive", "sv-choice", () => { if (!menu.current()) return; menu.shell.dismiss(); openArchive({ parent: composer }); }, "layers", "Archive and Highlights"),
          button("Edit Close Friends", "sv-choice", () => { if (!menu.current()) return; menu.shell.dismiss(); openCloseFriends({ parent: composer }); }, "users", "Close Friends list"),
        );
        if (!resume && draftRecord?.journalRevision) menu.body.append(button("Delete saved Story draft", "sv-choice sv-danger", () => {
          if (!menu.current()) return;
          menu.shell.dismiss(); void deleteDraft();
        }, "trash", "Delete saved draft"));
        if (!resume && autosaveError) menu.body.append(button("Close without saving", "sv-choice", async () => {
          if (!menu.current() || confirming) return;
          confirming = true;
          try {
            if (!(await hooks.confirm?.("Close now? Changes that have not finished saving may be lost. This does not delete the last saved copy.", { confirm: "Close without saving" }))) return;
            if (menu.current()) finish();
          } catch (_) { if (menu.current()) say("The choice could not be confirmed. Your editor is still open.", true); }
          finally { confirming = false; }
        }, "x", "Close without saving"));
      }

      function draftMessage(message, error = false) {
        if (!composer.alive() || autosaveStopped || resume) return;
        if (draftStatus.textContent !== message) draftStatus.textContent = message;
        draftStatus.classList.toggle("is-error", error);
      }

      function stopAutosave() {
        autosaveStopped = true;
        if (autosaveTimer !== null) draftClock.clearTimeout(autosaveTimer);
        autosaveTimer = null;
      }

      function queueAutosave(delay = 400) {
        if (!composer.alive() || autosaveStopped || resume) return;
        if (autosaveTimer !== null) draftClock.clearTimeout(autosaveTimer);
        autosaveTimer = null;
        if (!autosaveWork && !autosaveError && savedSignature === draftSignature() && draftRecord?.journalRevision) {
          draftMessage("Applied edits saved on this device."); return;
        }
        draftMessage("Changes not yet saved.");
        autosaveTimer = draftClock.setTimeout(() => {
          autosaveTimer = null;
          // Completion schedules the latest snapshot; do not accumulate waiters
          // for every keystroke or drag while a browser write is slow.
          if (!autosaveWork) void persistDraft();
        }, delay);
      }

      async function persistDraft({ verify = false } = {}) {
        if (autosaveTimer !== null) draftClock.clearTimeout(autosaveTimer);
        autosaveTimer = null;
        while (autosaveWork) await autosaveWork;
        if (!composer.alive() || autosaveStopped || resume) return false;
        const signature = draftSignature();
        if (!verify && !autosaveError && savedSignature === signature && draftRecord?.journalRevision) {
          draftMessage("Applied edits saved on this device."); return true;
        }
        let record;
        const work = Promise.resolve().then(async () => {
          let succeeded = false;
          try {
            if (!composer.alive() || autosaveStopped || resume) return false;
            const problem = validateDraft(draft);
            if (problem) throw recoveryError(problem);
            const writtenSignature = draftSignature();
            const next = { file: draft.file, audience: "public", caption: draft.caption, elements: JSON.parse(JSON.stringify(draft.elements)) };
            draftRecord ||= postingJob(next);
            record = draftRecord; record.draft = next; record.action = "draft";
            autosaveError = false; draftMessage("Saving draft on this device…");
            watchLeave(true, record);
            await journal(record);
            savedSignature = writtenSignature;
            succeeded = true;
            if (savedSignature === draftSignature()) draftMessage("Applied edits saved on this device.");
          } catch (error) {
            autosaveError = true;
            draftMessage(error?.message || "The draft could not be saved. Keep this tab open and use Save draft to try again.", true);
          } finally {
            if (record) watchLeave(false, record);
            autosaveWork = null;
            if (succeeded && composer.alive() && !autosaveStopped && savedSignature !== draftSignature()) {
              if (doc.visibilityState === "hidden") void Promise.resolve().then(() => persistDraft());
              else queueAutosave();
            }
          }
          return succeeded;
        });
        autosaveWork = work;
        return work;
      }

      function backgroundDraft() {
        if (doc.visibilityState === "hidden" && !autosaveWork) void persistDraft();
      }

      function saving(active) {
        draft.busy = active;
        const shareButtons = [...shareRow.children];
        for (const control of [close, ...tools.children, ...edit.children, saveDraftButton, ...shareButtons]) {
          control.disabled = active || (shareButtons.includes(control) && Boolean(draft.problem));
        }
      }

      async function saveDraft() {
        if (!composer.current() || !shell.isCurrent() || confirming || resume) return;
        const problem = validateDraft(draft);
        if (problem) { say(problem, true); return; }
        saving(true);
        saveDraftButton.setAttribute("aria-busy", "true");
        try {
          const saved = await persistDraft({ verify: true });
          if (!composer.alive()) return;
          if (!saved) { say(draftStatus.textContent || "The draft could not be saved. Keep this tab open.", true); return; }
          finish(); say("Draft saved in this browser. Nothing was shared.");
        } catch (error) {
          if (!composer.alive()) return;
          status.textContent = error?.message || "The draft could not be saved. Keep this tab open and try again.";
          status.classList.add("is-error"); say(status.textContent, true);
        } finally {
          saving(false);
          saveDraftButton.removeAttribute("aria-busy");
        }
      }

      async function deleteDraft() {
        if (!composer.current() || confirming || !draftRecord?.journalRevision) return;
        confirming = true;
        let removing = false;
        try {
          if (!(await hooks.confirm?.("Delete this local draft and the changes in this editor? This does not delete any published Story.", { confirm: "Delete draft", destructive: true }))) return;
          if (!composer.current()) return;
          removing = true; stopAutosave();
          saving(true);
          if (await clearRecovery(draftRecord) && composer.alive()) { finish(); say("Local draft deleted. Published Stories are unchanged."); }
        } catch (error) { if (composer.alive()) say(error?.message || "The draft could not be deleted.", true); }
        finally {
          if (removing && composer.alive()) { autosaveStopped = false; autosaveError = true; }
          saving(false); confirming = false;
        }
      }

      function publish(audience) {
        if (!composer.current() || !shell.isCurrent() || draft.busy || confirming) return;
        if (!canWrite()) { say("Posting is paused right now. Your draft is still here.", true); return; }
        const problem = validateDraft(draft) || draft.problem;
        if (problem) {
          status.textContent = problem;
          status.classList.add("is-error");
          say(problem, true);
          return;
        }
        draft.busy = true;
        stopAutosave();
        const next = { file: draft.file, audience, caption: draft.caption, elements: draft.elements.map(element => ({ ...element })) };
        if (!resume) {
          startPosting(next, draftRecord);
        } else if (state.posting === resume && resume.failed && resume.owner === viewerId()) {
          resume.draft = next;
          void runPosting(resume);
        } else {
          // Signed in as someone else since, or already being shared: this editor posts nothing.
          say("This story is no longer waiting to be shared.", true);
        }
        finish();
      }

      function finish() {
        if (draft.closed) return;
        stopAutosave();
        doc.removeEventListener?.("visibilitychange", backgroundDraft);
        draft.closed = true;
        watchLeave(false, composer);
        for (const child of [...composer.childSheets]) child.shell.dismiss();
        stopDrag();
        if (isVideo) { media.pause(); media.removeAttribute("src"); media.load(); }
        URL.revokeObjectURL(url);
        dismissShell();
        if (state.composer === composer) state.composer = null;
      }

      function select(index) {
        if (!composer.current()) return;
        draft.selected = index;
        edit.hidden = index < 0;
        editSelected.hidden = draft.elements[index]?.type !== "text";
        for (const [position, child] of [...layer.children].entries()) {
          child.classList.toggle("is-selected", position === index);
          child.setAttribute("aria-pressed", String(position === index));
        }
      }

      function announcePosition(element) {
        if (draft.problem) return;
        status.textContent = `${Math.round(clamp(element.x, 0, 1) * 100)}% from left, ${Math.round(clamp(element.y, 0, 1) * 100)}% from top. Size ${Math.round((element.scale ?? 1) * 100)}%.`;
      }

      function resize(delta) {
        if (!composer.current()) return;
        const element = draft.elements[draft.selected];
        if (!element) return;
        element.scale = clamp((element.scale ?? 1) + delta, 0.4, 3);
        paint();
        announcePosition(element);
      }

      function removeSelected() {
        if (!composer.current()) return;
        if (draft.selected < 0) return;
        const restore = (layer.contains?.(doc.activeElement) || edit.contains?.(doc.activeElement));
        const index = draft.selected;
        draft.elements.splice(draft.selected, 1);
        draft.selected = Math.min(index, draft.elements.length - 1);
        paint({ focusIndex: restore ? draft.selected : null });
        if (!draft.problem) status.textContent = "Sticker removed.";
      }

      function addElement(element) {
        if (!composer.current()) return;
        if (draft.elements.length >= MAX_ELEMENTS) { say(`A Story can hold up to ${MAX_ELEMENTS} stickers.`, true); return; }
        draft.elements.push({ x: 0.5, y: 0.45, scale: 1, rotation: 0, ...element });
        paint();
        select(draft.elements.length - 1);
      }

      function paint({ focusIndex = null } = {}) {
        if (!composer.current()) return;
        const focused = [...layer.children].indexOf(doc.activeElement);
        stopDrag();
        layer.replaceChildren();
        draft.elements.forEach((element, index) => {
          const rendered = renderElement(element, { editing: true });
          rendered.tabIndex = 0;
          rendered.setAttribute("role", "button");
          const name = element.type === "text" ? `Text sticker: ${element.text || ""}`
            : element.type === "emoji" ? `Emoji sticker: ${element.emoji || ""}`
              : element.type === "mention" ? `Mention sticker: @${element.user?.handle || element.handle || "someone"}`
                : element.type === "poll" ? `Poll sticker: ${element.question || ""}. ${(element.options || []).join(" or ")}`
                  : `Link sticker: ${element.label || element.url || "Link"}`;
          rendered.setAttribute("aria-label", name);
          rendered.setAttribute("aria-describedby", instructions.id);
          rendered.addEventListener("pointerdown", event => startDrag(event, index, rendered));
          rendered.addEventListener("click", () => { if (composer.current() && layer.children[index] === rendered) select(index); });
          rendered.addEventListener("keydown", event => {
            if (!composer.current() || layer.children[index] !== rendered || event.altKey || event.ctrlKey || event.metaKey) return;
            const step = event.shiftKey ? 0.05 : 0.01;
            const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
            if (moves[event.key]) {
              event.preventDefault();
              element.x = clamp(element.x + moves[event.key][0], 0, 1);
              element.y = clamp(element.y + moves[event.key][1], 0, 1);
              position(rendered, element);
              announcePosition(element);
              queueAutosave();
            } else if (event.key === "Delete" || event.key === "Backspace") {
              event.preventDefault();
              select(index);
              removeSelected();
            } else if (event.key === " " || event.key === "Enter") {
              event.preventDefault(); select(index);
            } else if (event.key === "F2" && element.type === "text") {
              event.preventDefault(); editText(index);
            }
          });
          rendered.addEventListener("focus", () => { if (layer.children[index] === rendered) select(index); });
          rendered.addEventListener("dblclick", () => { if (layer.children[index] === rendered && element.type === "text") editText(index); });
          layer.append(rendered);
        });
        select(Math.min(draft.selected, draft.elements.length - 1));
        const target = focusIndex ?? (focused >= 0 ? Math.min(focused, draft.elements.length - 1) : null);
        if (target !== null && shell.isCurrent()) (layer.children[target] || addTextTool).focus({ preventScroll: true });
        queueAutosave();
      }

      function startDrag(event, index, rendered) {
        if (!composer.current() || layer.children[index] !== rendered) return;
        stopDrag();
        event.preventDefault();
        select(index);
        const element = draft.elements[index];
        const box = layer.getBoundingClientRect();
        const move = moveEvent => {
          if (!composer.current() || layer.children[index] !== rendered) return;
          element.x = clamp((moveEvent.clientX - box.left) / box.width, 0, 1);
          element.y = clamp((moveEvent.clientY - box.top) / box.height, 0, 1);
          position(rendered, element);
          queueAutosave();
        };
        const up = () => {
          doc.removeEventListener("pointermove", move);
          doc.removeEventListener("pointerup", up);
          doc.removeEventListener("pointercancel", up);
          stopDrag = () => {};
        };
        doc.addEventListener("pointermove", move);
        doc.addEventListener("pointerup", up);
        doc.addEventListener("pointercancel", up);
        stopDrag = up;
      }

      function editText(index = -1) {
        if (!composer.current()) return;
        const existing = index >= 0 ? draft.elements[index] : null;
        const editor = accountSheet(existing ? "Edit text" : "Add text", composer);
        let edited = existing;
        editor.shell.returnFocus = () => composer.current() ? layer.children[draft.elements.indexOf(edited)] || addTextTool : null;
        const input = node("textarea", "sv-text-input");
        input.maxLength = 120;
        input.rows = 3;
        input.value = existing?.text || "";
        input.setAttribute("aria-label", "Story text");
        let color = Object.hasOwn(TEXT_COLORS, existing?.color) ? existing.color : "white";
        let style = TEXT_STYLES.includes(existing?.style) ? existing.style : "box";
        const colors = node("div", "sv-swatches");
        colors.setAttribute("role", "radiogroup");
        colors.setAttribute("aria-label", "Text colour");
        for (const [name, value] of Object.entries(TEXT_COLORS)) {
          const swatch = button(`${name} text`, "sv-swatch", () => { if (!editor.current()) return; color = name; paintChoices(); });
          swatch.style.setProperty("--swatch", value);
          swatch.dataset.color = name;
          swatch.setAttribute("role", "radio");
          swatch.addEventListener("keydown", event => {
            if (!editor.current() || !editor.shell.isCurrent() || event.altKey || event.ctrlKey || event.metaKey) return;
            const choices = [...colors.children], current = choices.indexOf(swatch);
            let next;
            if (["ArrowRight", "ArrowDown"].includes(event.key)) next = (current + 1) % choices.length;
            else if (["ArrowLeft", "ArrowUp"].includes(event.key)) next = (current + choices.length - 1) % choices.length;
            else if (event.key === "Home") next = 0;
            else if (event.key === "End") next = choices.length - 1;
            else return;
            event.preventDefault(); event.stopPropagation();
            color = choices[next].dataset.color; paintChoices(); choices[next].focus({ preventScroll: true });
          });
          colors.append(swatch);
        }
        const styles = node("div", "sv-style-choices");
        for (const name of TEXT_STYLES) {
          const choice = button(`${name} style`, "sv-chip", () => { if (!editor.current()) return; style = name; paintChoices(); }, "", name === "plain" ? "Plain" : name === "box" ? "Box" : "Outline");
          choice.dataset.style = name;
          styles.append(choice);
        }
        const done = node("button", "publish-button", "Done");
        done.type = "button";
        done.addEventListener("click", () => {
          if (!editor.current()) return;
          const text = input.value.trim();
          if (!text) { editor.shell.dismiss(); return; }
          if (existing) Object.assign(existing, { text, color, style });
          else { addElement({ type: "text", text, color, style }); edited = draft.elements.at(-1); }
          paint();
          editor.shell.dismiss();
        });
        function paintChoices() {
          for (const swatch of colors.children) {
            const selected = swatch.dataset.color === color;
            swatch.setAttribute("aria-checked", String(selected));
            swatch.tabIndex = selected ? 0 : -1;
          }
          for (const choice of styles.children) choice.setAttribute("aria-pressed", String(choice.dataset.style === style));
        }
        paintChoices();
        editor.body.append(input, colors, styles, done);
        editor.shell.focusLater(input);
      }

      function openStickers() {
        if (!composer.current()) return;
        const picker = accountSheet("Stickers", composer);
        const grid = node("div", "sv-emoji-grid");
        for (const emoji of STICKER_EMOJI) {
          const choice = button(`Add ${emoji}`, "sv-emoji", () => { if (!picker.current()) return; addElement({ type: "emoji", emoji, scale: 1.4 }); picker.shell.dismiss(); }, "", emoji);
          grid.append(choice);
        }
        const specials = node("div", "sv-special-stickers");
        specials.append(
          button("Mention someone", "sv-chip", () => { if (!picker.current()) return; picker.shell.dismiss(); addMention(); }, "user", "Mention"),
          button("Add a link", "sv-chip", () => { if (!picker.current()) return; picker.shell.dismiss(); addLink(); }, "link", "Link"),
          button("Add a poll", "sv-chip", () => { if (!picker.current()) return; picker.shell.dismiss(); addPoll(); }, "list", "Poll"),
        );
        picker.body.append(specials, grid);
      }

      function addMention() {
        if (!composer.current()) return;
        pickPerson("Mention someone", user => {
          if (!composer.current()) return;
          if (draft.elements.filter(e => e.type === "mention").length >= 5) { say("A Story can mention up to 5 people.", true); return; }
          addElement({ type: "mention", user_id: user.id, handle: user.handle });
        }, { parent: composer });
      }

      function addLink() {
        if (!composer.current()) return;
        if (draft.elements.some(e => e.type === "link")) { say("A Story can hold one link.", true); return; }
        const form = accountSheet("Add a link", composer);
        const address = node("input", "sv-field");
        address.type = "url";
        address.placeholder = "https://";
        address.setAttribute("aria-label", "Link address");
        const label = node("input", "sv-field");
        label.type = "text";
        label.maxLength = 40;
        label.placeholder = "Link text (optional)";
        label.setAttribute("aria-label", "Link text");
        const done = node("button", "publish-button", "Add link");
        done.type = "button";
        done.addEventListener("click", () => {
          if (!form.current()) return;
          const href = safeUrl(address.value.trim());
          if (!href) { say("Enter a full web address that starts with https://", true); return; }
          addElement({ type: "link", url: href, label: label.value.trim(), y: 0.8 });
          form.shell.dismiss();
        });
        form.body.append(address, label, done);
        form.shell.focusLater(address);
      }

      function addPoll() {
        if (!composer.current()) return;
        if (draft.elements.some(e => e.type === "poll")) { say("A Story can hold one poll.", true); return; }
        const form = accountSheet("Add a poll", composer);
        const question = node("input", "sv-field");
        question.maxLength = 80;
        question.placeholder = "Ask a question";
        question.setAttribute("aria-label", "Poll question");
        const first = node("input", "sv-field");
        first.maxLength = 25;
        first.value = "Yes";
        first.setAttribute("aria-label", "First answer");
        const second = node("input", "sv-field");
        second.maxLength = 25;
        second.value = "No";
        second.setAttribute("aria-label", "Second answer");
        const done = node("button", "publish-button", "Add poll");
        done.type = "button";
        done.addEventListener("click", () => {
          if (!form.current()) return;
          const values = [question.value.trim(), first.value.trim(), second.value.trim()];
          if (values.some(value => !value)) { say("A poll needs a question and two answers.", true); return; }
          if (values[1].toLowerCase() === values[2].toLowerCase()) { say("The two answers must differ.", true); return; }
          addElement({ type: "poll", question: values[0], options: [values[1], values[2]], y: 0.6 });
          form.shell.dismiss();
        });
        form.body.append(question, first, second, done);
        form.shell.focusLater(question);
      }

      if (!resume) doc.addEventListener?.("visibilitychange", backgroundDraft);
      paint();
    }

    function position(element, data) {
      element.style.left = `${clamp(data.x, 0, 1) * 100}%`;
      element.style.top = `${clamp(data.y, 0, 1) * 100}%`;
      element.style.transform = `translate(-50%, -50%) rotate(${clamp(data.rotation ?? 0, -180, 180)}deg) scale(${clamp(data.scale ?? 1, 0.4, 3)})`;
    }

    function renderElement(element, { editing = false, item = null, onVote = null, attempt = null, onRetryVote = null } = {}) {
      const wrapper = node("div", `sv-element sv-element-${element.type}`);
      position(wrapper, element);
      if (element.type === "text") {
        wrapper.classList.add(`is-${element.style || "plain"}`);
        wrapper.style.setProperty("--text-colour", TEXT_COLORS[element.color] || TEXT_COLORS.white);
        wrapper.append(node("span", "", element.text || ""));
      } else if (element.type === "emoji") {
        wrapper.append(node("span", "sv-emoji-art", element.emoji || ""));
        wrapper.setAttribute("aria-label", "Emoji sticker");
      } else if (element.type === "mention") {
        const user = element.user;
        const text = user ? `@${user.handle}` : element.handle ? `@${element.handle}` : "@someone";
        if (!editing && user) {
          const link = button(`Open ${user.display_name || user.handle}'s profile`, "sv-mention", () => hooks.openProfile?.(user.handle), "", text);
          wrapper.append(link);
        } else wrapper.append(node("span", "sv-mention", text));
      } else if (element.type === "link") {
        const href = safeUrl(element.url);
        const label = element.label || (href ? new URL(href).hostname : "Link");
        if (!editing && href) {
          const anchor = node("a", "sv-link");
          anchor.href = href;
          anchor.target = "_blank";
          anchor.rel = "noopener noreferrer nofollow ugc";
          anchor.append(svg("link"), node("span", "", label));
          anchor.addEventListener("click", event => event.stopPropagation());
          wrapper.append(anchor);
        } else {
          const span = node("span", "sv-link");
          span.append(svg("link"), node("span", "", label));
          wrapper.append(span);
        }
      } else if (element.type === "poll") {
        const card = node("div", "sv-poll");
        if (!editing) { card.tabIndex = -1; card.setAttribute("role", "group"); card.setAttribute("aria-label", element.question || "Story poll"); wrapper.pollFocusTarget = card; }
        card.append(node("strong", "", element.question || ""));
        const options = node("div", "sv-poll-options");
        const poll = pollConfirmed(item?.poll) ? item.poll : attempt?.poll || item?.poll || {};
        const counts = pollCountsValid(poll) ? poll.counts : null;
        const total = counts ? counts[0] + counts[1] : 0;
        (element.options || []).slice(0, 2).forEach((label, index) => {
          const choice = node(editing ? "span" : "button", "sv-poll-option");
          if (!editing) choice.type = "button";
          const text = node("span", "", label);
          choice.append(text);
          if (counts) {
            const share = total ? Math.round((counts[index] / total) * 100) : 0;
            choice.style.setProperty("--share", `${share}%`);
            choice.classList.add("has-result");
            choice.append(node("span", "sv-poll-share", `${share}%`));
            if (poll.viewer_vote === index) choice.classList.add("is-chosen");
            choice.setAttribute("aria-label", `${label}, ${share} percent`);
          }
          choice.disabled = editing || !onVote || Boolean(attempt) || poll.viewer_vote !== undefined && poll.viewer_vote !== null || item?.is_own
            || item?.live === false || Date.parse(item?.expires_at) <= Date.now();
          choice.addEventListener("click", event => { event.stopPropagation(); if (!choice.disabled && choice.isConnected !== false) onVote?.(index); });
          options.append(choice);
        });
        card.append(options);
        if (item?.is_own && counts) card.append(node("small", "", `${total} ${total === 1 ? "vote" : "votes"}`));
        if (!editing && attempt) {
          const confirmed = pollConfirmed(poll), expired = item?.live === false || Date.parse(item?.expires_at) <= Date.now();
          const status = node("p", "sv-poll-status", confirmed ? `Vote recorded: ${element.options[poll.viewer_vote]}.`
            : attempt.running ? `Sending your vote: ${element.options[attempt.option] || "your choice"}…`
              : expired ? "This Story has ended. Your vote was not confirmed here."
                : `Vote not confirmed. Try your ${element.options[attempt.option] || "original"} vote again.`);
          status.id = `story-poll-status-${hooks.uuid()}`; status.setAttribute("role", "status");
          card.setAttribute("aria-describedby", status.id); card.append(status);
          if (!confirmed && attempt.error && !attempt.running && !expired && onRetryVote) {
            const retry = button("Retry Story poll vote", "sv-poll-retry", () => { if (retry.isConnected !== false && !retry.disabled) onRetryVote(); }, "refresh", "Try vote again");
            card.append(retry); wrapper.pollFocusTarget = retry;
          }
        }
        wrapper.append(card);
      }
      return wrapper;
    }

    // Viewer -------------------------------------------------------------------------------

    let viewerRequest = 0;
    let viewerRequestOwner = null;
    let viewerRead = null;

    function cancelViewerRead() {
      viewerRequest += 1;
      viewerRead?.panel.shell.dismiss();
    }

    function cancelViewer() {
      cancelViewerRead();
      state.viewer?.close();
    }

    const viewableResult = item => item && typeof item.id === "string" && Boolean(item.id)
      && typeof item.author?.id === "string" && Boolean(item.author.id)
      && ["image", "video"].includes(item.media?.type) && typeof item.media.url === "string" && Boolean(item.media.url);

    async function openViewerRead(url, prepare) {
      cancelViewerRead();
      state.viewer?.pause();
      const owner = viewerId(), request = ++viewerRequest;
      viewerRequestOwner = owner;
      const panel = accountSheet("Opening Story");
      const message = node("p", "sv-hint", "Loading Story…"); message.setAttribute("role", "status");
      const retry = button("Retry opening Story", "sv-choice", () => { if (!retry.hidden) void run(); }, "refresh", "Try again");
      const signIn = button("Sign in to open Story", "sv-choice", () => { if (!current() || signIn.hidden) return; panel.shell.dismiss(); hooks.signIn?.(); }, "user", "Sign in");
      const cancel = button("Cancel opening Story", "sv-choice", () => panel.shell.dismiss(), "x", "Cancel");
      retry.hidden = true; signIn.hidden = true; panel.body.append(message, retry, signIn, cancel);
      const read = { panel, busy: false };
      viewerRead = read;
      const current = () => viewerRead === read && request === viewerRequest && owner === viewerId() && panel.current();
      const dismiss = panel.shell.dismiss;
      function finish(cancelled = true) {
        if (viewerRead === read) { viewerRead = null; if (cancelled) viewerRequest += 1; }
        dismiss();
      }
      panel.shell.dismiss = () => finish();
      async function run() {
        if (!current() || read.busy) return;
        read.busy = true; retry.hidden = true; retry.disabled = true; signIn.hidden = true;
        message.textContent = "Loading Story…"; message.classList.remove("is-error");
        try {
          const result = await hooks.api(url, { signal: panel.controller.signal });
          if (!current()) return;
          const context = prepare(result);
          if (!Array.isArray(context.items) || !context.items.length) throw new Error(context.emptyMessage || "This story is no longer available.");
          const ids = new Set();
          for (const item of context.items) {
            if (!viewableResult(item) || item.author.id !== context.author?.id || ids.has(item.id)) throw new Error("The Story could not be confirmed. Try again.");
            ids.add(item.id);
          }
          finish(false);
          if (request === viewerRequest && owner === viewerId()) showViewer(context);
        } catch (error) {
          if (!current()) return;
          message.textContent = Number(error?.status) === 404 ? "This story is no longer available." : error?.message || "The story could not open. Try again.";
          message.classList.add("is-error");
          signIn.hidden = Number(error?.status) !== 401;
          retry.hidden = !signIn.hidden;
          panel.shell.focusLater(signIn.hidden ? retry : signIn);
        } finally { read.busy = false; if (current()) retry.disabled = false; }
      }
      await run();
    }

    async function openAuthor(authorId, { queue = [] } = {}) {
      const capturedQueue = [...queue];
      return openViewerRead(`/api/story-items/by-author/${encodeURIComponent(authorId)}`, result => {
        if (result?.author?.id !== authorId || !Array.isArray(result?.items)) throw new Error("The Story list could not be confirmed. Try again.");
        return { items: result.items, start: Math.max(0, result.items.findIndex(item => !item.seen)), author: result.author, queue: capturedQueue, authorId };
      });
    }

    async function openItem(itemId) {
      return openViewerRead(`/api/story-items/${encodeURIComponent(itemId)}`, item => {
        if (item?.id !== itemId) throw new Error("The Story could not be confirmed. Try again.");
        return { items: [item], start: 0, author: item.author, queue: [], authorId: item.author?.id };
      });
    }

    async function openHighlight(highlightId) {
      return openViewerRead(`/api/story-highlights/${encodeURIComponent(highlightId)}`, result => {
        if (result?.id !== highlightId || !Array.isArray(result?.items)) throw new Error("The Highlight could not be confirmed. Try again.");
        return { items: result.items, start: 0, author: result.owner, queue: [], authorId: result.owner?.id, highlight: result, emptyMessage: "This Highlight is empty." };
      });
    }

    function showViewer(context) {
      state.viewer?.close();
      const shell = overlay("sv-viewer", `Story from ${context.author?.display_name || context.author?.handle || "a member"}`);
      const bars = node("div", "sv-progress");
      const header = node("header", "sv-viewer-header");
      const identity = node("button", "sv-identity");
      identity.type = "button";
      const who = node("span", "sv-identity-text");
      const nameLine = node("strong");
      const ageLine = node("small");
      who.append(nameLine, ageLine);
      identity.append(avatar(context.author), who);
      identity.addEventListener("click", event => { event.stopPropagation(); close(); hooks.openProfile?.(context.author?.handle); });
      const pause = button("Pause", "sv-icon-button", () => togglePause(), "pause");
      const more = button("More options", "sv-icon-button", () => openMenu(), "more");
      const closeButton = button("Close story", "sv-icon-button", () => close(), "x");
      header.append(identity, pause, more, closeButton);
      const stage = node("div", "sv-stage sv-view-stage");
      const layer = node("div", "sv-layer");
      const caption = node("p", "sv-view-caption");
      const footer = node("footer", "sv-viewer-footer");
      const live = node("p", "sr-only");
      live.setAttribute("aria-live", "polite");
      const mediaStatus = node("div", "sv-media-status");
      mediaStatus.setAttribute("role", "status");
      shell.append(bars, stage, header, caption, footer, live);
      shell.addEventListener("sv-close", () => close());

      let index = clamp(context.start, 0, context.items.length - 1);
      let timerStart = 0;
      let elapsedBefore = 0;
      let paused = false;
      let frame = 0;
      let mediaNode = null;
      let closed = false;
      let pointer = null;
      let ready = false;
      let mediaWait = null;
      let playRequest = 0;
      const owner = viewerId();
      let detachReply = () => {};
      const pollChanged = id => { if (!closed && viewerId() === owner && item()?.id === id) paintPoll(); };
      pollListeners.add(pollChanged);
      const pendingDeletes = new Set();
      const viewerSheets = new Set();
      let highlightPicker = null;
      let deleteControl = null;
      const itemCurrent = current => !closed && viewerId() === owner && item() === current;

      function viewerSheet(title, current) {
        const panel = sheet(title);
        panel.controller = new AbortController();
        viewerSheets.add(panel);
        const dismiss = panel.shell.dismiss;
        panel.shell.dismiss = () => {
          if (!viewerSheets.delete(panel)) return;
          panel.controller.abort();
          dismiss();
        };
        panel.current = () => viewerSheets.has(panel) && itemCurrent(current);
        panel.shell.addEventListener("sv-close", () => {
          if (itemCurrent(current)) resumePlayback();
        }, { once: true });
        return panel;
      }

      function dismissViewerSheets() {
        for (const panel of [...viewerSheets].reverse()) panel.shell.dismiss();
      }

      const foreground = () => doc.visibilityState !== "hidden";
      const activeMedia = media => !closed && viewerId() === owner && mediaNode === media;

      function clearMediaWait() {
        draftClock.clearTimeout(mediaWait);
        mediaWait = null;
      }

      function waitForMedia(media) {
        clearMediaWait();
        mediaWait = draftClock.setTimeout(() => { if (!ready) mediaFailed(media); }, 20000);
      }

      function mediaMessage(text, action = null, label = "Retry story") {
        mediaStatus.replaceChildren(node("p", "", text));
        if (action) mediaStatus.append(button(label, "sv-choice", action));
        mediaStatus.hidden = false;
      }

      function markSeen(current) {
        if (current.is_own || current.seen || current.live === false || current === pendingSeen) return;
        pendingSeen = current;
        hooks.api(`/api/story-items/${encodeURIComponent(current.id)}/seen`, { method: "POST", csrf: true })
          .then(() => {
            if (viewerId() !== owner) return;
            current.seen = true;
            void refreshTray();
          })
          .catch(() => { /* An unacknowledged receipt remains retryable on the next opening. */ })
          .finally(() => { if (pendingSeen === current) pendingSeen = null; });
      }
      let pendingSeen = null;

      function loaded(media, current) {
        if (!activeMedia(media)) return;
        clearMediaWait();
        if (!ready) timerStart = performance.now();
        ready = true;
        mediaStatus.hidden = true;
        if (foreground()) markSeen(current);
        else pausePlayback();
      }

      function mediaFailed(media) {
        if (!activeMedia(media)) return;
        clearMediaWait();
        ready = false;
        pausePlayback();
        mediaMessage("This story could not load. Check your connection and try again.", () => {
          if (!activeMedia(media)) return;
          resumePlaybackState();
          render();
        });
      }

      function playVideo(media, mutedRetry = true) {
        if (!activeMedia(media) || paused || !foreground()) return;
        const request = ++playRequest;
        media.play?.().catch(() => {
          if (!activeMedia(media) || paused || !foreground() || request !== playRequest) return;
          if (mutedRetry && !media.muted) {
            media.muted = true;
            playVideo(media, false);
          } else {
            clearMediaWait();
            pausePlayback();
            mediaMessage("Tap to play this video.", () => {
              if (activeMedia(media)) resumePlayback();
            }, "Play video");
          }
        });
      }

      function item() { return context.items[index]; }

      function paintBars() {
        bars.replaceChildren();
        context.items.forEach((_, position) => {
          const bar = node("span", "sv-bar");
          const fill = node("span", "sv-bar-fill");
          fill.style.transform = `scaleX(${position < index ? 1 : 0})`;
          bar.append(fill);
          bars.append(bar);
        });
      }

      function render() {
        const current = item();
        if (!current) { close(); return; }
        dismissViewerSheets();
        paintBars();
        nameLine.textContent = current.author?.display_name || current.author?.handle || "";
        ageLine.textContent = [relativeAge(current.created_at), current.close_friends ? "Close Friends" : "",
          context.highlight ? context.highlight.title : ""].filter(Boolean).join(" · ");
        shell.classList.toggle("is-close-friends", Boolean(current.close_friends));
        clearMediaWait();
        const previousMedia = mediaNode;
        mediaNode = null;
        if (previousMedia?.tagName === "VIDEO") { previousMedia.pause(); previousMedia.removeAttribute("src"); previousMedia.load(); }
        ready = false;
        stage.replaceChildren();
        stage.classList.remove("is-contained");
        const isVideo = current.media?.type === "video";
        mediaNode = isVideo ? node("video", "sv-media") : node("img", "sv-media");
        const media = mediaNode;
        mediaMessage("Loading story…");
        media.addEventListener("error", () => mediaFailed(media));
        if (!isVideo && current.media?.url) stage.append(backdrop(current.media.url));
        watchFit(stage, mediaNode, current.media?.width, current.media?.height, () => activeMedia(media));
        if (isVideo) {
          mediaNode.setAttribute("playsinline", "");
          mediaNode.muted = Boolean(hooks.muted?.());
          media.addEventListener("playing", () => {
            if (!activeMedia(media)) return;
            if (paused || !foreground()) { media.pause(); return; }
            loaded(media, current);
          });
          media.addEventListener("ended", () => { if (activeMedia(media) && ready && !paused && foreground()) next(); });
        } else {
          media.alt = current.caption || "Story photo";
          media.addEventListener("load", () => {
            if (media.naturalWidth > 0) loaded(media, current);
            else mediaFailed(media);
          });
        }
        paintPoll();
        stage.append(mediaNode, layer, mediaStatus);
        caption.textContent = current.caption || "";
        caption.hidden = !current.caption;
        renderFooter(current);
        live.textContent = `Story ${index + 1} of ${context.items.length}`;
        elapsedBefore = 0;
        timerStart = performance.now();
        const pendingPoll = pollAttempt(current);
        if (pendingPoll && !pendingPoll.poll && (pendingPoll.running || pendingPoll.error)) pausePlayback();
        if (!isVideo || !paused && foreground()) waitForMedia(media);
        if (current.media?.url) {
          media.src = current.media.url;
          if (isVideo) playVideo(media);
          else if (media.complete && media.naturalWidth > 0) loaded(media, current);
        } else mediaFailed(media);
        if (!foreground()) pausePlayback();
        cancelAnimationFrame(frame);
        tick();
      }

      function renderFooter(current) {
        detachReply();
        detachReply = () => {};
        deleteControl = null;
        footer.replaceChildren();
        footer.classList.toggle("sv-own-footer", Boolean(current.is_own));
        if (current.is_own) {
          const seen = button(`Seen by ${current.viewer_count || 0}`, "sv-seen", () => void showViewers(current), "users",
            `${current.viewer_count || 0} viewers`);
          const add = button("Add to your story", "sv-footer-action", () => { close(); openCreate(); }, "plus", "Add");
          const highlight = button("Add to a Highlight", "sv-footer-action", () => addToHighlight(current), "bookmark", "Highlight");
          const remove = button("Delete this story", "sv-footer-action sv-danger", () => void deleteItem(current), "trash", "Delete");
          remove.disabled = pendingDeletes.has(current.id);
          deleteControl = { current, button: remove };
          // Views are kept while a Story is live; an archived or highlighted one has no list.
          footer.append(...(current.live === false ? [] : [seen]), add, highlight, remove);
          return;
        }
        if (!canWrite() || current.live === false) return;
        const record = replyRecord(current);
        const form = node("form", "sv-reply");
        const input = node("input", "sv-reply-input");
        input.type = "text";
        input.maxLength = 2000;
        input.placeholder = `Reply to ${current.author?.display_name || current.author?.handle || "this story"}`;
        input.setAttribute("aria-label", "Reply privately");
        input.value = record.draft;
        input.addEventListener("input", () => {
          if (itemCurrent(current) && !record.busy && !record.attempt) record.draft = input.value;
        });
        input.addEventListener("focus", () => pausePlayback());
        input.addEventListener("blur", () => { if (!input.value) resumePlayback(); });
        const send = button("Send reply", "sv-icon-button", () => form.requestSubmit(), "send");
        form.append(input, send);
        form.addEventListener("submit", event => {
          event.preventDefault();
          if (!itemCurrent(current) || record.busy) return;
          if (!record.attempt) record.draft = input.value;
          const text = record.attempt?.body || record.draft.trim();
          if (text) void reply(current, text, record, "text");
        });
        const quick = node("div", "sv-quick-reactions");
        // Instagram shows the quick reactions once you start a reply; here they no longer sit over the Story.
        const openQuick = () => quick.classList.add("is-open");
        input.addEventListener("focus", openQuick);
        if (record.draft || record.attempt || record.busy) openQuick();
        for (const emoji of QUICK_REACTIONS) {
          quick.append(button(`React with ${emoji}`, "sv-quick", () => {
            if (!record.busy && !record.attempt) void reply(current, emoji, record, "reaction");
          }, "", emoji));
        }
        const status = node("p", "sv-reply-status");
        status.setAttribute("role", "status");
        const paint = () => {
          if (!itemCurrent(current)) return;
          input.readOnly = record.busy || Boolean(record.attempt);
          input.value = record.draft;
          send.disabled = record.busy;
          send.setAttribute("aria-label", record.busy ? "Sending reply" : record.attempt ? "Retry reply" : "Send reply");
          form.setAttribute("aria-busy", String(record.busy));
          for (const reaction of quick.children) reaction.disabled = record.busy || Boolean(record.attempt);
          status.textContent = record.message;
          status.hidden = !record.message;
        };
        record.paint = paint;
        detachReply = () => { if (record.paint === paint) record.paint = null; };
        footer.append(quick, form, status);
        paint();
        if (record.draft || record.attempt || record.busy) pausePlayback();
      }

      function tick() {
        if (closed) return;
        const current = item();
        const duration = itemDurationMs(current);
        const elapsed = paused || !ready ? elapsedBefore : elapsedBefore + (performance.now() - timerStart);
        const fill = bars.children[index]?.firstChild;
        const progressValue = mediaNode?.tagName === "VIDEO" && mediaNode.duration
          ? mediaNode.currentTime / mediaNode.duration
          : Math.min(1, elapsed / duration);
        if (fill) fill.style.transform = `scaleX(${progressValue})`;
        if (ready && !paused && foreground() && mediaNode?.tagName !== "VIDEO" && elapsed >= duration) { next(); return; }
        frame = requestAnimationFrame(tick);
      }

      function pausePlayback() {
        if (paused) return;
        paused = true;
        if (ready) elapsedBefore += performance.now() - timerStart;
        if (mediaNode?.tagName === "VIDEO") {
          playRequest += 1;
          mediaNode.pause();
          if (!ready) clearMediaWait();
        }
        pause.setAttribute("aria-label", "Play");
        pause.replaceChildren(svg("play"));
      }

      function resumePlayback() {
        if (closed || viewerId() !== owner || !foreground() || !paused) return;
        paused = false;
        timerStart = performance.now();
        if (mediaNode?.tagName === "VIDEO") {
          if (!ready) waitForMedia(mediaNode);
          playVideo(mediaNode);
        }
        else if (ready) markSeen(item());
        pause.setAttribute("aria-label", "Pause");
        pause.replaceChildren(svg("pause"));
      }

      function togglePause() { if (paused) resumePlayback(); else pausePlayback(); }

      function next() {
        if (closed || viewerId() !== owner) return;
        if (index < context.items.length - 1) { index += 1; resumePlaybackState(); render(); return; }
        const queue = context.queue || [];
        const position = queue.indexOf(context.authorId);
        const following = position >= 0 ? queue[position + 1] : null;
        close();
        if (following) void openAuthor(following, { queue });
      }

      function previous() {
        if (closed || viewerId() !== owner) return;
        if (index > 0) { index -= 1; resumePlaybackState(); render(); return; }
        elapsedBefore = 0;
        timerStart = performance.now();
      }

      function resumePlaybackState() {
        paused = false;
        pause.setAttribute("aria-label", "Pause");
        pause.replaceChildren(svg("pause"));
      }

      stage.addEventListener("pointerdown", event => {
        if (event.target.closest("button, a, input")) return;
        pointer = { x: event.clientX, y: event.clientY, at: performance.now() };
        pausePlayback();
      });
      stage.addEventListener("pointerup", event => {
        if (!pointer) return;
        const dx = event.clientX - pointer.x;
        const dy = event.clientY - pointer.y;
        const held = performance.now() - pointer.at > 350;
        pointer = null;
        if (dy > 90 && Math.abs(dy) > Math.abs(dx)) { close(); return; }
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) { resumePlayback(); if (dx < 0) next(); else previous(); return; }
        if (held) { resumePlayback(); return; }
        resumePlayback();
        const box = stage.getBoundingClientRect();
        if (event.clientX - box.left < box.width * 0.33) previous(); else next();
      });
      stage.addEventListener("pointercancel", () => { pointer = null; resumePlayback(); });
      shell.addEventListener("keydown", event => {
        if (event.target.isContentEditable || event.target.closest("button, a, input, textarea, select, [contenteditable='true'], [role='button'], [role='radio']")) return;
        if (event.key === "ArrowRight") { event.preventDefault(); next(); }
        else if (event.key === "ArrowLeft") { event.preventDefault(); previous(); }
        else if (event.key === " ") { event.preventDefault(); togglePause(); }
      });

      function paintPoll() {
        const current = item();
        if (!itemCurrent(current)) return;
        const restore = layer.contains?.(doc.activeElement);
        const attempt = pollAttempt(current);
        const elements = (current.elements || []).map(element => renderElement(element, {
          item: current, attempt, onVote: option => void vote(current, option),
          onRetryVote: attempt ? () => void vote(current, attempt.option) : null,
        }));
        layer.replaceChildren(...elements);
        if (restore && shell.isCurrent()) elements.find(element => element.pollFocusTarget)?.pollFocusTarget.focus({ preventScroll: true });
      }

      async function vote(current, optionIndex) {
        if (!itemCurrent(current) || current.is_own || current.live === false || Date.parse(current.expires_at) <= Date.now()
          || current.poll?.viewer_vote != null || ![0, 1].includes(optionIndex)) return;
        let record = pollAttempt(current);
        if (record && (record.running || record.poll || record.option !== optionIndex)) return;
        if (!record) { record = { owner, option: optionIndex, running: false, error: "", poll: null }; pollRecords.set(current.id, record); }
        const recordCurrent = () => viewerId() === record.owner && pollRecords.get(current.id) === record;
        record.running = true; record.error = "";
        pausePlayback(); notifyPoll(current.id);
        try {
          const result = await hooks.api(`/api/story-items/${encodeURIComponent(current.id)}/poll-vote`, {
            method: "PUT", csrf: true, json: { option_index: optionIndex },
          });
          if (!recordCurrent()) return;
          if (result?.story_item_id !== current.id || !pollConfirmed(result.poll)
            || result.poll.total !== result.poll.counts[0] + result.poll.counts[1]) throw new Error("Your vote could not be confirmed. Try again.");
          record.poll = result.poll; current.poll = result.poll;
        } catch (error) {
          if (recordCurrent()) {
            record.error = error?.message || "Your vote could not be confirmed. Try again.";
            if (itemCurrent(current)) say(record.error, true);
          }
        } finally {
          record.running = false;
          if (recordCurrent()) notifyPoll(current.id);
        }
      }

      async function reply(current, text, record, kind) {
        if (!itemCurrent(current) || record.busy || !canWrite()) return;
        const accountCurrent = () => viewerId() === owner && replyRecords.get(current.id) === record;
        if (!record.attempt) record.attempt = {
          body: text, kind, token: hooks.uuid(), conversationId: null, dispatched: false, uncertain: false,
        };
        const attempt = record.attempt;
        record.busy = true;
        record.message = attempt.uncertain ? "Checking the previous reply…" : "Sending…";
        pausePlayback();
        record.paint?.();
        try {
          if (!attempt.conversationId) {
            const opened = await hooks.api("/api/messages/conversations/open", {
              method: "POST", csrf: true, json: { recipient_user_id: current.author.id },
            });
            if (!accountCurrent()) return;
            if (!itemCurrent(current)) { record.attempt = null; record.message = ""; return; }
            attempt.conversationId = opened?.conversation?.id;
            if (!attempt.conversationId) throw new Error("The conversation could not open.");
          }
          attempt.dispatched = true;
          const message = await hooks.api(`/api/messages/conversations/${encodeURIComponent(attempt.conversationId)}/messages`, {
            method: "POST", csrf: true,
            json: { body: attempt.body, story_item_id: current.id, send_token: attempt.token },
          });
          if (!accountCurrent()) return;
          const review = message?.accepted === true && ["pending_review", "review_complete"].includes(message.status)
            && typeof message.quarantine_id === "string" && Boolean(message.quarantine_id);
          if (!review && !(typeof message?.id === "string" && message.id
            && message.conversation_id === attempt.conversationId)) {
            throw new Error("The reply could not be confirmed.");
          }
          record.attempt = null;
          if (attempt.kind === "text" && record.draft.trim() === attempt.body) record.draft = "";
          record.message = review
            ? message.status === "pending_review" ? "Reply sent for review. It has not been delivered."
              : "Review finished. This reply attempt was not delivered."
            : attempt.kind === "reaction" ? "Reaction sent" : "Reply sent";
          if (itemCurrent(current)) {
            say(record.message);
            if (!review && !record.draft) resumePlayback();
          }
        } catch (error) {
          if (!accountCurrent()) return;
          const rejected = Number(error?.status) >= 400 && Number(error?.status) < 500 && Number(error?.status) !== 408;
          if (!attempt.dispatched || rejected && !attempt.uncertain) {
            record.attempt = null;
            record.message = error?.message || "Your reply was not sent. Try again.";
          } else {
            attempt.uncertain = true;
            record.message = "Delivery is unconfirmed. Retry reply checks the same message without sending a duplicate.";
          }
        } finally {
          record.busy = false;
          if (accountCurrent()) record.paint?.();
        }
      }

      function addToHighlight(current) {
        if (!itemCurrent(current) || !current.is_own || highlightPicker?.current()) return;
        pausePlayback();
        const panel = viewerSheet("Add to a Highlight", current);
        highlightPicker = panel;
        const dismiss = panel.shell.dismiss;
        panel.shell.dismiss = () => { if (highlightPicker === panel) highlightPicker = null; dismiss(); };
        const status = node("p", "sv-hint");
        status.setAttribute("role", "status");
        const list = node("div");
        const reload = button("Retry Highlights", "secondary-button", () => void load());
        const retry = button("Retry Highlight addition", "secondary-button", () => void save());
        const make = button("New Highlight", "sv-choice", () => {
          if (!panel.current() || busy || attempt) return;
          panel.shell.dismiss(); close(); editHighlight(null, [current.id]);
        }, "plus", "New Highlight");
        panel.body.append(status, list, reload, retry, make);
        let highlights = [], loading = false, loadFailed = false, busy = false, attempt = null;
        function paint() {
          if (!panel.current()) return;
          reload.hidden = !loadFailed;
          reload.disabled = loading || busy || Boolean(attempt);
          retry.hidden = !attempt;
          retry.disabled = busy;
          make.disabled = busy || Boolean(attempt);
          list.setAttribute("aria-busy", String(loading || busy));
          list.replaceChildren();
          for (const highlight of highlights) {
            const add = button(`Add to ${highlight.title}`, "sv-choice", () => {
              if (!panel.current() || loading || busy || attempt) return;
              attempt = { highlight, uncertain: false };
              void save();
            }, "bookmark", highlight.title);
            add.disabled = loading || busy || Boolean(attempt);
            list.append(add);
          }
        }
        async function load() {
          if (!panel.current() || loading || busy || attempt) return;
          loading = true; loadFailed = false;
          status.textContent = "Loading Highlights…";
          paint();
          try {
            const result = await hooks.api(`/api/story-highlights/by-owner/${encodeURIComponent(owner)}`, { signal: panel.controller.signal });
            if (!panel.current()) return;
            if (!Array.isArray(result?.items) || result.items.some(entry => !entry?.id || typeof entry.title !== "string")) {
              throw new Error("The Highlights could not be loaded.");
            }
            highlights = result.items;
            status.textContent = highlights.length ? "Choose a Highlight for this Story." : "No Highlights yet. Create your first one.";
          } catch (error) {
            if (!panel.current()) return;
            loadFailed = true;
            status.textContent = error?.message || "Highlights could not load. Try again.";
          } finally { loading = false; paint(); }
        }
        async function save() {
          if (!panel.current() || busy || loading || !attempt) return;
          const saving = attempt;
          busy = true;
          status.textContent = `Adding to ${saving.highlight.title}…`;
          paint();
          try {
            const result = await hooks.api(`/api/story-highlights/${encodeURIComponent(saving.highlight.id)}`, {
              method: "PATCH", csrf: true, json: { add_story_item_ids: [current.id] },
            });
            if (!panel.current()) return;
            if (result?.id !== saving.highlight.id || result.is_own !== true
              || !Array.isArray(result.story_item_ids) || !result.story_item_ids.includes(current.id)) {
              throw new Error("The addition could not be confirmed.");
            }
            say(`Added to ${result.title || saving.highlight.title}`);
            panel.shell.dismiss(); resumePlayback();
            hooks.onHighlightsChanged?.();
          } catch (error) {
            if (!panel.current()) return;
            const rejected = Number(error?.status) >= 400 && Number(error?.status) < 500 && Number(error?.status) !== 408;
            if (rejected && !saving.uncertain) attempt = null;
            else saving.uncertain = true;
            status.textContent = attempt ? "Addition is unconfirmed. Retry the same Highlight, or close and check your profile."
              : error?.message || "The Story was not added.";
            say(status.textContent, true);
          } finally { busy = false; paint(); }
        }
        void load();
      }

      async function showViewers(current) {
        if (!itemCurrent(current) || !current.is_own) return;
        pausePlayback();
        const panel = viewerSheet("Viewers", current);
        panel.body.append(node("p", "sv-hint", "Only you can see who viewed your story."));
        const status = node("p", "sv-hint", "Loading viewers…");
        status.setAttribute("role", "status");
        const list = node("ul", "sv-people");
        const more = button("Load more Story viewers", "sv-choice", () => void load(), "", "Load more");
        const retry = button("Retry Story viewers", "sv-choice", () => void load(), "refresh", "Try again");
        const reload = button("Reload Story viewers", "sv-choice", () => void load(true), "refresh", "Reload");
        panel.body.append(status, list, more, retry, reload);
        const seen = new Set(), positions = new Set();
        let busy = false, before = null, hasMore = false, total = 0, loaded = false, identitiesEnded = current.live === false;
        let expiryTimer = null;
        const dismiss = panel.shell.dismiss;
        panel.shell.dismiss = () => {
          if (expiryTimer !== null) draftClock.clearTimeout(expiryTimer);
          expiryTimer = null; doc.removeEventListener("visibilitychange", checkExpiry); dismiss();
        };
        function controls() {
          more.hidden = !hasMore || identitiesEnded; more.disabled = busy;
          retry.disabled = busy; reload.disabled = busy;
          if (!busy && ((doc.activeElement === more && more.hidden) || (doc.activeElement === retry && retry.hidden))) panel.shell.focusLater(reload);
        }
        function summary() {
          status.classList.remove("is-error");
          status.textContent = identitiesEnded ? `${loaded ? `${total} ${total === 1 ? "view" : "views"}. ` : ""}Viewer names are no longer available after this Story expires.`
            : !total ? "No views yet."
              : `${seen.size} ${seen.size === 1 ? "viewer" : "viewers"} listed · ${total} total ${total === 1 ? "view" : "views"}.${!hasMore && total > seen.size ? " Some accounts are not listed." : ""}`;
        }
        function endIdentities() {
          if (expiryTimer !== null) draftClock.clearTimeout(expiryTimer);
          expiryTimer = null;
          if (!panel.current()) return;
          identitiesEnded = true; list.replaceChildren(); seen.clear(); positions.clear(); before = null; hasMore = false;
          retry.hidden = true; summary(); controls();
        }
        const expires = Date.parse(current.expires_at);
        function checkExpiry() { if (Number.isFinite(expires) && expires <= Date.now()) endIdentities(); }
        doc.addEventListener("visibilitychange", checkExpiry);
        if (Number.isFinite(expires) && !identitiesEnded) {
          if (expires <= Date.now()) endIdentities();
          else expiryTimer = draftClock.setTimeout(endIdentities, Math.min(2147483647, expires - Date.now()));
        }
        async function load(reset = false) {
          if (!panel.current() || busy || (!reset && loaded && !hasMore && retry.hidden)) return;
          busy = true; retry.hidden = true; controls();
          const position = reset ? null : before;
          try {
            const result = await hooks.api(`/api/story-items/${encodeURIComponent(current.id)}/viewers${position ? `?before=${encodeURIComponent(position)}` : ""}`, { signal: panel.controller.signal });
            if (!panel.current()) return;
            if (!result || !Array.isArray(result.items) || !Number.isInteger(result.total) || result.total < 0
              || result.items.some(row => !row?.viewer || typeof row.viewer.id !== "string" || !row.viewer.id)
              || (result.identities_available !== undefined && typeof result.identities_available !== "boolean")
              || (result.has_more !== undefined && typeof result.has_more !== "boolean")
              || (result.story_item_id !== undefined && result.story_item_id !== current.id)) throw new Error("The viewer list could not be confirmed. Try again.");
            const next = result.has_more === true ? result.next_before : null;
            if (next !== null && (typeof next !== "string" || !next || next === position || (!reset && positions.has(next)))) {
              throw new Error("The next viewer page could not be confirmed. Reload the list.");
            }
            if (reset) { list.replaceChildren(); seen.clear(); positions.clear(); }
            total = result.total; loaded = true;
            if (result.identities_available === false || identitiesEnded || (Number.isFinite(expires) && expires <= Date.now())) { endIdentities(); return; }
            before = next; hasMore = next !== null;
            if (position) positions.add(position);
            for (const row of result.items) {
              if (seen.has(row.viewer.id)) continue;
              seen.add(row.viewer.id);
              const entry = node("li", "sv-person");
              entry.append(avatar(row.viewer), node("span", "", row.viewer.display_name || row.viewer.handle || "Member"));
              if ([0, 1].includes(row.poll_vote)) {
                const poll = (current.elements || []).find(element => element.type === "poll");
                entry.append(node("small", "sv-vote-note", poll?.options?.[row.poll_vote] ? `Voted ${poll.options[row.poll_vote]}` : "Voted"));
              }
              list.append(entry);
            }
            summary();
          } catch (error) {
            if (!panel.current()) return;
            if ([401, 403, 404].includes(Number(error?.status))) {
              list.replaceChildren(); seen.clear(); positions.clear(); before = null; hasMore = false; loaded = false;
            }
            status.textContent = error?.message || "Viewers could not load. Try again."; status.classList.add("is-error");
            retry.hidden = false;
          } finally { if (panel.current()) { busy = false; controls(); } }
        }
        more.hidden = true; retry.hidden = true;
        void load(true);
      }

      async function deleteItem(current) {
        if (!itemCurrent(current) || !current.is_own || pendingDeletes.has(current.id)) return;
        pendingDeletes.add(current.id);
        if (deleteControl?.current === current) deleteControl.button.disabled = true;
        pausePlayback();
        try {
          const confirmed = await hooks.confirm?.("Delete this story?", {
            body: "It is removed from your story, your archive and any Highlight.", confirm: "Delete", destructive: true,
          });
          if (!itemCurrent(current)) return;
          if (!confirmed) { resumePlayback(); return; }
          const result = await hooks.api(`/api/story-items/${encodeURIComponent(current.id)}`, { method: "DELETE", csrf: true });
          if (result?.removed !== true) throw new Error("The deletion could not be confirmed. Try again.");
          removeFromViewer(current, "Story deleted");
        } catch (error) {
          if (Number(error?.status) === 404) removeFromViewer(current, "This story is no longer available.");
          else if (itemCurrent(current)) {
            say(error?.message || "The deletion could not be confirmed. Try again.", true);
          }
        } finally {
          pendingDeletes.delete(current.id);
          if (itemCurrent(current) && deleteControl?.current === current) deleteControl.button.disabled = false;
        }
      }

      function removeFromViewer(current, message) {
        if (viewerId() !== owner) return;
        void refreshTray();
        if (closed) return;
        const selected = item();
        const removedIndex = context.items.findIndex(entry => entry.id === current.id);
        if (removedIndex < 0) return;
        context.items.splice(removedIndex, 1);
        if (selected === current) say(message);
        if (!context.items.length) { close(); return; }
        if (selected !== current) {
          index = context.items.indexOf(selected);
          paintBars();
          live.textContent = `Story ${index + 1} of ${context.items.length}`;
          return;
        }
        index = Math.min(removedIndex, context.items.length - 1);
        resumePlaybackState();
        render();
      }

      function openMenu() {
        pausePlayback();
        const current = item();
        const menu = viewerSheet("Story options", current);
        const done = () => { if (!menu.current()) return; menu.shell.dismiss(); resumePlayback(); };
        if (current.is_own) {
          menu.body.append(
            button("Open your archive", "sv-choice", () => { if (!menu.current()) return; menu.shell.dismiss(); close(); openArchive(); }, "layers", "Archive and Highlights"),
            button("Edit Close Friends", "sv-choice", () => { if (!menu.current()) return; menu.shell.dismiss(); close(); openCloseFriends(); }, "users", "Close Friends"),
          );
        } else {
          menu.body.append(
            button("Report this story", "sv-choice sv-danger", () => { if (!menu.current()) return; menu.shell.dismiss(); void report(current); }, "flag", "Report"),
            button("Mute or block", "sv-choice", () => { if (!menu.current()) return; menu.shell.dismiss(); close(); hooks.openProfile?.(current.author?.handle); }, "user", "Open profile"),
          );
        }
        menu.body.append(button("Cancel", "sv-choice", done, "", "Cancel"));
      }

      async function report(current) {
        if (!itemCurrent(current)) return;
        const picker = viewerSheet("Report this story", current);
        let busy = false;
        const choices = [];
        picker.body.append(node("p", "sv-hint", "Reports are private. The author is not told who reported."));
        for (const [value, label] of REPORT_CATEGORIES) {
          const choice = button(label, "sv-choice", async () => {
            if (!picker.current() || busy) return;
            busy = true;
            for (const entry of choices) entry.disabled = true;
            try {
              const result = await hooks.api("/api/safety/reports", {
                method: "POST", csrf: true,
                json: { target_type: "story_item", target_id: current.id, category: value },
              });
              if (!picker.current()) return;
              if (typeof result?.id !== "string" || !result.id) throw new Error("The report could not be confirmed. Try again.");
              say("Thanks. Our team will review this story.");
              picker.shell.dismiss();
              resumePlayback();
            } catch (error) {
              if (picker.current()) say(error?.message || "The report could not be confirmed. Try again.", true);
            } finally {
              busy = false;
              if (picker.current()) for (const entry of choices) entry.disabled = false;
            }
          }, "", label);
          choices.push(choice);
          picker.body.append(choice);
        }
      }

      function close() {
        if (closed) return;
        closed = true;
        pollListeners.delete(pollChanged);
        dismissViewerSheets();
        detachReply();
        cancelViewerRead();
        clearMediaWait();
        doc.removeEventListener?.("visibilitychange", visibilityChanged);
        cancelAnimationFrame(frame);
        if (mediaNode?.tagName === "VIDEO") { mediaNode.pause(); mediaNode.removeAttribute("src"); mediaNode.load(); }
        shell.dismiss();
        if (state.viewer?.shell === shell) state.viewer = null;
        hooks.onViewerClosed?.();
      }

      state.viewer = { shell, close, owner, pause: pausePlayback };
      function visibilityChanged() { if (!foreground()) pausePlayback(); }
      doc.addEventListener?.("visibilitychange", visibilityChanged);
      render();
      closeButton.focus({ preventScroll: true });
    }

    // Highlights, archive and Close Friends --------------------------------------------------

    async function openArchive({ selectedIds = [], onSelect = null, parent = null } = {}) {
      if (!viewerId()) { hooks.signIn?.(); return; }
      if (parent && !parent.current()) return;
      archivePanel?.shell.dismiss();
      const panel = accountSheet("Story archive", parent);
      archivePanel = panel;
      panel.body.append(node("p", "sv-hint", "Only you can see your archive. Pick Stories to keep on your profile as a Highlight."));
      const grid = node("div", "sv-archive-grid");
      panel.body.append(grid);
      const actions = node("div", "sv-sheet-actions");
      const make = node("button", "publish-button", "New Highlight");
      make.type = "button";
      make.setAttribute("aria-label", onSelect ? "Apply Story selection" : "New Highlight from selected Stories");
      if (onSelect) make.textContent = "Use selection";
      make.disabled = true;
      const more = button("Load older Stories", "secondary-button", () => void load());
      const progress = node("p", "sv-hint");
      progress.setAttribute("role", "status");
      const error = node("p", "form-error");
      error.setAttribute("role", "alert");
      error.hidden = true;
      actions.append(more, make);
      panel.body.append(progress, error);
      panel.body.append(actions);
      const chosen = new Set(selectedIds);
      const loaded = new Set();
      const loadedItems = new Map();
      const cursors = new Set();
      let before = null;
      let busy = false;
      let finished = false;
      function selectionChanged() {
        make.disabled = chosen.size === 0;
        progress.textContent = `${loaded.size} Stories loaded · ${chosen.size} of ${MAX_HIGHLIGHT_ITEMS} selected`;
      }
      make.addEventListener("click", () => {
        if (!panel.current() || !chosen.size) return;
        panel.shell.dismiss();
        if (onSelect) onSelect([...chosen], loadedItems);
        else editHighlight(null, [...chosen]);
      });
      function appendItem(item) {
        if (loaded.has(item.id)) return;
        loaded.add(item.id);
        loadedItems.set(item.id, item);
        const tile = node("button", "sv-archive-tile");
        tile.type = "button";
        tile.setAttribute("aria-pressed", String(chosen.has(item.id)));
        tile.setAttribute("data-story-id", item.id);
        tile.setAttribute("aria-label", `Story from ${new Date(item.created_at).toLocaleDateString()}`);
        const picture = cover(item.media);
        if (picture) tile.append(picture);
        tile.append(node("span", "sv-archive-date", new Date(item.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })));
        if (item.close_friends) tile.classList.add("is-close-friends");
        tile.addEventListener("click", () => {
          if (!panel.current()) return;
          if (chosen.has(item.id)) chosen.delete(item.id);
          else {
            if (chosen.size >= MAX_HIGHLIGHT_ITEMS) { say(`A Highlight can hold up to ${MAX_HIGHLIGHT_ITEMS} Stories.`, true); return; }
            chosen.add(item.id);
          }
          tile.setAttribute("aria-pressed", String(chosen.has(item.id)));
          selectionChanged();
        });
        grid.append(tile);
      }
      async function load() {
        if (!panel.current() || busy || finished) return;
        busy = true;
        more.disabled = true;
        more.textContent = "Loading…";
        error.hidden = true;
        grid.setAttribute("aria-busy", "true");
        try {
          const result = await hooks.api(`/api/story-items/archive${before ? `?before=${encodeURIComponent(before)}` : ""}`, {
            signal: panel.controller.signal,
          });
          if (!panel.current()) return;
          const next = result?.next_before ?? null;
          if (!Array.isArray(result?.items) || result.items.some(item => typeof item?.id !== "string" || !item.id)
            || next !== null && (typeof next !== "string" || !next || next === before || cursors.has(next)
              || !result.items.some(item => item.id === next))) {
            throw new Error("The next archive page could not be read. Try again.");
          }
          for (const item of result.items) appendItem(item);
          if (!loaded.size) grid.append(node("p", "sv-empty", "Your Stories will appear here."));
          before = next;
          if (next) cursors.add(next);
          finished = next === null;
          more.hidden = finished;
          selectionChanged();
        } catch (failure) {
          if (!panel.current()) return;
          error.textContent = failure?.message || "The archive could not load. Try again.";
          error.hidden = false;
          more.setAttribute("aria-label", "Retry archive page");
        } finally {
          busy = false;
          if (panel.current()) {
            grid.setAttribute("aria-busy", "false");
            more.disabled = false;
            more.textContent = error.hidden ? "Load older Stories" : "Retry archive page";
            if (error.hidden) more.setAttribute("aria-label", "Load older Stories");
          }
        }
      }
      await load();
    }

    function editHighlight(highlight, itemIds = []) {
      if (!viewerId()) return;
      if (highlightCreateOwner !== viewerId()) { highlightCreates.clear(); highlightCreateOwner = viewerId(); }
      const creationKey = JSON.stringify(itemIds);
      if (!highlight && !highlightCreates.has(creationKey)) highlightCreates.set(creationKey, {
        title: "", attempt: null, busy: false, message: "", done: false, paint: null,
      });
      const creation = highlight ? null : highlightCreates.get(creationKey);
      const panel = accountSheet(highlight ? "Edit Highlight" : "New Highlight");
      const title = node("input", "sv-field");
      title.maxLength = 40;
      title.value = highlight?.title || creation?.title || "";
      title.placeholder = "Highlight name";
      title.setAttribute("aria-label", "Highlight name");
      const save = node("button", "publish-button", highlight ? "Save" : "Create Highlight");
      save.type = "button";
      const status = node("p", "sv-hint");
      status.setAttribute("role", "status");
      const editing = highlight ? { ready: false, loading: false, busy: false, title: highlight.title || "",
        ids: [], selected: new Set(), items: new Map(), attempt: null, uncertain: false, notice: "" } : null;
      const contents = node("ul", "sv-highlight-items");
      const addStories = button("Choose Stories from archive", "secondary-button", () => {
        if (!panel.current() || !editing?.ready || editing.busy || editing.attempt) return;
        void openArchive({ parent: panel, selectedIds: [...editing.selected], onSelect: (ids, items) => {
          if (!panel.current() || editing.busy || editing.attempt) return;
          editing.selected = new Set(ids);
          editing.notice = "";
          for (const [id, item] of items) editing.items.set(id, item);
          paintEditing();
        } });
      });
      const reload = button("Reload saved Highlight", "secondary-button", () => void loadEditing(true));
      let deleteHighlightControl = null;
      function paintEditing() {
        if (!editing || !panel.current()) return;
        save.disabled = !editing.ready || editing.loading || editing.busy || !editing.selected.size;
        save.textContent = editing.busy ? "Saving…" : editing.attempt ? "Retry changes" : "Save";
        title.readOnly = editing.busy || Boolean(editing.attempt);
        addStories.disabled = !editing.ready || editing.loading || editing.busy || Boolean(editing.attempt);
        reload.disabled = editing.loading || editing.busy;
        reload.hidden = editing.ready && !editing.attempt;
        if (deleteHighlightControl) deleteHighlightControl.disabled = editing.loading || editing.busy;
        contents.setAttribute("aria-busy", String(editing.loading));
        contents.replaceChildren();
        const ordered = [...editing.ids.filter(id => editing.selected.has(id)),
          ...[...editing.selected].filter(id => !editing.ids.includes(id))];
        for (const [index, id] of ordered.entries()) {
          const item = editing.items.get(id);
          const row = node("li", "sv-highlight-item");
          row.setAttribute("data-story-id", id);
          const picture = cover(item?.media);
          if (picture) row.append(picture);
          row.append(node("span", "", item?.media
            ? item.caption || `Story ${index + 1}` : `Unavailable media · Story ${index + 1}`));
          const remove = button(`Remove Story ${index + 1} from Highlight`, "sv-icon-button", () => {
            if (!panel.current() || editing.busy || editing.attempt || editing.loading) return;
            editing.selected.delete(id);
            editing.notice = "";
            paintEditing();
          }, "x");
          remove.disabled = editing.busy || editing.loading || Boolean(editing.attempt);
          row.append(remove); contents.append(row);
        }
        if (editing.notice && !editing.loading && !editing.busy) {
          status.textContent = editing.notice; status.hidden = false;
        } else if (editing.ready && !editing.selected.size) {
          status.textContent = "Keep at least one Story, or delete this Highlight.";
          status.hidden = false;
        } else if (editing.ready && !editing.attempt && !editing.busy && !editing.loading) {
          status.textContent = `${editing.selected.size} Stories. New Stories are added at the end.`;
          status.hidden = false;
        }
      }
      async function loadEditing(resetTitle = false) {
        if (!editing || !panel.current() || editing.loading || editing.busy) return;
        editing.loading = true;
        status.textContent = "Loading Highlight…"; status.hidden = false;
        paintEditing();
        try {
          const result = await hooks.api(`/api/story-highlights/${encodeURIComponent(highlight.id)}`, { signal: panel.controller.signal });
          if (!panel.current()) return;
          if (result?.id !== highlight.id || result.is_own !== true || !Array.isArray(result.story_item_ids)
            || !Array.isArray(result.items) || result.story_item_ids.some(id => typeof id !== "string" || !id)
            || new Set(result.story_item_ids).size !== result.story_item_ids.length) throw new Error("The Highlight could not be loaded.");
          if (resetTitle || title.value === editing.title) title.value = result.title;
          editing.title = result.title;
          editing.ids = [...result.story_item_ids];
          editing.selected = new Set(editing.ids);
          editing.items = new Map(result.items.map(item => [item.id, item]));
          editing.ready = true; editing.attempt = null; editing.uncertain = false; editing.notice = "";
        } catch (error) {
          if (panel.current()) { editing.notice = error?.message || "The Highlight could not load. Try again."; }
        } finally { editing.loading = false; paintEditing(); }
      }
      title.addEventListener("input", () => {
        if (creation && panel.current() && !creation.attempt) creation.title = title.value;
        if (editing && panel.current() && !editing.attempt) { editing.notice = ""; paintEditing(); }
      });
      if (creation) {
        const paint = () => {
          if (!panel.current()) return;
          title.value = creation.title;
          title.readOnly = Boolean(creation.attempt);
          save.disabled = creation.busy;
          save.textContent = creation.busy ? "Saving…" : creation.attempt ? "Retry creation" : "Create Highlight";
          status.textContent = creation.message;
          status.hidden = !creation.message;
          if (creation.done) {
            say(creation.message);
            panel.shell.dismiss();
            hooks.onHighlightsChanged?.();
          }
        };
        creation.paint = paint;
        const dismiss = panel.shell.dismiss;
        panel.shell.dismiss = () => { if (creation.paint === paint) creation.paint = null; dismiss(); };
      } else status.hidden = true;
      save.addEventListener("click", async () => {
        if (!panel.current() || save.disabled) return;
        const name = title.value.trim();
        if (!name) { say("Give the Highlight a name.", true); return; }
        if (creation) {
          if (creation.busy) return;
          if (!creation.attempt) {
            creation.title = name;
            creation.attempt = { token: hooks.uuid(), title: name, ids: [...itemIds], uncertain: false };
          }
          const attempt = creation.attempt;
          const accountCurrent = () => viewerId() === panel.owner && highlightCreates.get(creationKey) === creation;
          creation.busy = true;
          creation.message = attempt.uncertain ? "Checking the previous creation…" : "Saving…";
          creation.paint?.();
          try {
            const result = await hooks.api("/api/story-highlights", {
              method: "POST", csrf: true,
              json: { title: attempt.title, story_item_ids: attempt.ids, create_token: attempt.token },
            });
            if (!accountCurrent()) return;
            if (typeof result?.id !== "string" || !result.id || result.creation?.token !== attempt.token
              || typeof result.creation.available !== "boolean") throw new Error("The creation could not be confirmed.");
            creation.done = true;
            creation.message = !result.creation.available ? "This Highlight was already deleted. It was not recreated."
              : result.creation.replayed ? "Your Highlight was already created." : "Highlight created";
            highlightCreates.delete(creationKey);
            creation.paint?.();
          } catch (error) {
            if (!accountCurrent()) return;
            const rejected = Number(error?.status) >= 400 && Number(error?.status) < 500 && Number(error?.status) !== 408;
            if (rejected && !attempt.uncertain) {
              creation.attempt = null;
              creation.message = error?.message || "The Highlight was not created. Check the details and try again.";
            } else {
              attempt.uncertain = true;
              creation.message = "Creation is unconfirmed. Retry creation checks the same request.";
            }
          } finally {
            creation.busy = false;
            if (accountCurrent()) creation.paint?.();
          }
          return;
        }
        if (!editing?.ready || editing.busy || !editing.selected.size) return;
        if (!editing.attempt) editing.attempt = {
          ...(name !== editing.title ? { title: name } : {}),
          add_story_item_ids: [...editing.selected].filter(id => !editing.ids.includes(id)),
          remove_story_item_ids: editing.ids.filter(id => !editing.selected.has(id)),
        };
        editing.busy = true;
        paintEditing();
        try {
          const result = await hooks.api(`/api/story-highlights/${encodeURIComponent(highlight.id)}`, {
            method: "PATCH", csrf: true, json: editing.attempt,
          });
          if (!panel.current()) return;
          if (result?.id !== highlight.id) throw new Error("The save could not be confirmed. Check your Highlights before trying again.");
          say(highlight ? "Highlight saved" : "Highlight created");
          panel.shell.dismiss();
          hooks.onHighlightsChanged?.();
        } catch (error) {
          if (panel.current()) {
            const rejected = Number(error?.status) >= 400 && Number(error?.status) < 500 && Number(error?.status) !== 408;
            if (rejected && !editing.uncertain) editing.attempt = null;
            else editing.uncertain = true;
            editing.notice = editing.attempt ? "Save is unconfirmed. Retry changes, or reload the saved Highlight."
              : error?.message || "The changes were not saved.";
            say(editing.notice, true);
          }
        } finally { editing.busy = false; paintEditing(); }
      });
      panel.body.append(title, save, status);
      creation?.paint?.();
      if (highlight) {
        panel.body.append(contents, addStories, reload);
        const remove = node("button", "secondary-button sv-danger", "Delete Highlight");
        deleteHighlightControl = remove;
        remove.type = "button";
        remove.addEventListener("click", async () => {
          if (!panel.current() || editing.loading || editing.busy || remove.disabled) return;
          editing.busy = true;
          paintEditing();
          remove.disabled = true;
          save.disabled = true;
          try {
            if (!(await hooks.confirm?.("Delete this Highlight?", { body: "The Stories stay in your archive.", confirm: "Delete", destructive: true }))) return;
            if (!panel.current()) return;
            const result = await hooks.api(`/api/story-highlights/${encodeURIComponent(highlight.id)}`, { method: "DELETE", csrf: true });
            if (!panel.current()) return;
            if (result?.deleted !== true) throw new Error("The Highlight deletion could not be confirmed.");
            say("Highlight deleted");
            panel.shell.dismiss();
            hooks.onHighlightsChanged?.();
          } catch (error) { if (panel.current()) say(error?.message || "The Highlight deletion could not be confirmed.", true); }
          finally { editing.busy = false; paintEditing(); }
        });
        panel.body.append(remove);
        void loadEditing();
      }
      panel.shell.focusLater(title);
    }

    async function renderHighlights(container, user, isSelf) {
      if (!container) return;
      const owner = user?.id;
      const viewer = viewerId();
      const token = (container.dataset.svToken = String((Number(container.dataset.svToken) || 0) + 1));
      const context = JSON.stringify([viewer, owner, Boolean(isSelf)]);
      const preserveVisible = container.dataset.svContext === context && !container.hidden;
      container.dataset.svContext = context;
      // Keep the loaded row in place while the same profile is revalidated.
      if (!preserveVisible || !owner || !viewer) {
        container.replaceChildren();
        container.hidden = true;
        delete container.dataset.svContentKey;
      }
      if (!owner || !viewer) return;
      const current = () => container.dataset.svToken === token && viewerId() === viewer;
      let items = [];
      try {
        const result = await hooks.api(`/api/story-highlights/by-owner/${encodeURIComponent(owner)}`);
        items = Array.isArray(result?.items) ? result.items : [];
      } catch (error) {
        if (!current()) return;
        if (preserveVisible && ![401, 403, 404].includes(error?.status)) return;
        container.replaceChildren();
        container.hidden = true;
        delete container.dataset.svContentKey;
        return;
      }
      if (!current()) return;
      const contentKey = JSON.stringify([context, items]);
      if (container.dataset.svContentKey === contentKey) return;
      container.dataset.svContentKey = contentKey;
      container.dataset.svEmpty = String(items.length === 0);
      container.replaceChildren();
      container.hidden = true;
      if (!items.length && !isSelf) return;
      for (const highlight of items) {
        const tile = node("button", "sv-highlight");
        tile.type = "button";
        tile.setAttribute("aria-label", `Highlight: ${highlight.title}`);
        const ring = node("span", "sv-highlight-ring");
        const picture = cover({ url: highlight.cover_url, thumbnail_url: highlight.cover_url,
          type: highlight.cover_type || "image" });
        if (highlight.cover_url && picture) ring.append(picture);
        tile.append(ring, node("span", "sv-highlight-title", highlight.title));
        tile.addEventListener("click", () => void openHighlight(highlight.id));
        if (isSelf) {
          tile.addEventListener("contextmenu", event => { event.preventDefault();
            if (viewerId() === viewer && container.dataset.svContext === context) editHighlight(highlight);
          });
        }
        container.append(tile);
      }
      if (isSelf) {
        const add = node("button", "sv-highlight sv-highlight-new");
        add.type = "button";
        add.setAttribute("aria-label", "New Highlight from your archive");
        const ring = node("span", "sv-highlight-ring");
        ring.append(svg("plus"));
        add.append(ring, node("span", "sv-highlight-title", items.length ? "New" : "Add a highlight"));
        add.addEventListener("click", () => void openArchive());
        container.append(add);
        if (items.length) {
          const manage = node("button", "sv-highlight sv-highlight-manage");
          manage.type = "button";
          manage.setAttribute("aria-label", "Edit Highlights");
          const manageRing = node("span", "sv-highlight-ring");
          manageRing.append(svg("edit"));
          manage.append(manageRing, node("span", "sv-highlight-title", "Edit"));
          manage.addEventListener("click", () => {
            if (viewerId() !== viewer || container.dataset.svContext !== context) return;
            const picker = accountSheet("Edit Highlights");
            for (const highlight of items) {
              picker.body.append(button(`Edit ${highlight.title}`, "sv-choice", () => {
                if (!picker.current()) return; picker.shell.dismiss(); editHighlight(highlight);
              }, "edit", highlight.title));
            }
          });
          container.append(manage);
        }
      }
      container.hidden = false;
    }

    function pickPerson(title, onPick, { excluded = new Set(), parent = null } = {}) {
      if (!viewerId() || (parent && !parent.current())) return null;
      const panel = accountSheet(title, parent);
      const input = node("input", "sv-field");
      input.type = "search";
      input.placeholder = "Search people";
      input.setAttribute("aria-label", "Search people");
      const results = node("ul", "sv-people");
      let request = 0;
      let timer = 0;
      const dismiss = panel.shell.dismiss;
      panel.shell.dismiss = () => { draftClock.clearTimeout(timer); request += 1; dismiss(); };
      input.addEventListener("input", () => {
        draftClock.clearTimeout(timer);
        const current = ++request;
        results.replaceChildren();
        const query = input.value.trim();
        if (!panel.current() || query.length < 2) return;
        results.append(node("li", "sv-hint", "Searching…"));
        timer = draftClock.setTimeout(async () => {
          if (!panel.current() || current !== request) return;
          try {
            const users = await hooks.searchPeople(query);
            if (!panel.current() || current !== request) return;
            if (!Array.isArray(users)) throw new Error("Invalid search results");
            results.replaceChildren();
            for (const user of users.filter(user => user.id !== viewerId() && !excluded.has(user.id)).slice(0, 12)) {
              const entry = node("li", "sv-person");
              const pick = button(`Choose ${user.display_name || user.handle}`, "sv-person-button", () => {
                if (!panel.current() || current !== request) return;
                panel.shell.dismiss(); onPick(user);
              });
              pick.textContent = "";
              pick.append(avatar(user), node("span", "", `${user.display_name || user.handle} @${user.handle}`));
              entry.append(pick);
              results.append(entry);
            }
            if (!results.children.length) results.append(node("li", "sv-empty", "Nobody found."));
          } catch (_) {
            if (!panel.current() || current !== request) return;
            results.replaceChildren(node("li", "form-error", "Search is unavailable right now."));
          }
        }, 250);
      });
      panel.body.append(input, results);
      panel.shell.focusLater(input);
      return panel;
    }

    async function openCloseFriends({ parent = null } = {}) {
      if (!viewerId()) { hooks.signIn?.(); return; }
      if (parent && !parent.current()) return;
      const panel = accountSheet("Close Friends", parent);
      panel.body.append(node("p", "sv-hint", "Close Friends Stories show a green ring and are seen only by these people. Nobody is told when you add or remove them."));
      const add = node("button", "publish-button", "Add people");
      add.type = "button";
      const list = node("ul", "sv-people");
      const status = node("p", "sv-hint");
      status.setAttribute("role", "status");
      const reload = button("Reload Close Friends", "secondary-button", () => void load());
      const retry = button("Retry Close Friends change", "secondary-button", () => void change());
      panel.body.append(add, status, list, retry, reload);
      let members = [];
      let ready = false, busy = false, loading = false, attempt = null;
      function paint() {
        if (!panel.current()) return;
        add.disabled = !ready || busy || loading || Boolean(attempt);
        reload.disabled = busy || loading;
        retry.hidden = !attempt;
        retry.disabled = busy || loading;
        list.setAttribute("aria-busy", String(loading || busy));
        list.replaceChildren();
        if (ready && !members.length) list.append(node("li", "sv-empty", "Your list is empty."));
        for (const entry of members) {
          const row = node("li", "sv-person");
          const remove = button(`Remove ${entry.user.display_name || entry.user.handle}`, "secondary-button", () => {
            if (!panel.current() || busy || loading || attempt) return;
            attempt = { user: entry.user, included: false, uncertain: false };
            void change();
          }, "", "Remove");
          remove.disabled = busy || loading || Boolean(attempt);
          row.append(avatar(entry.user), node("span", "", entry.user.display_name || entry.user.handle), remove);
          list.append(row);
        }
      }
      async function load() {
        if (!panel.current() || busy || loading) return;
        loading = true;
        status.textContent = "Loading Close Friends…";
        paint();
        try {
          const result = await hooks.api("/api/close-friends", { signal: panel.controller.signal });
          if (!panel.current()) return;
          if (!Array.isArray(result?.items) || result.items.some(entry => !entry?.user?.id)) throw new Error("The list could not be confirmed.");
          members = result.items;
          ready = true; attempt = null;
          status.textContent = "Your saved Close Friends list.";
        } catch (error) {
          if (panel.current()) status.textContent = error?.message || "The list could not load. Reload to try again.";
        } finally { loading = false; paint(); }
      }
      async function change() {
        if (!panel.current() || busy || loading || !attempt) return;
        const current = attempt;
        busy = true;
        status.textContent = current.included ? "Adding to Close Friends…" : "Removing from Close Friends…";
        paint();
        try {
          const result = await hooks.api(`/api/close-friends/${encodeURIComponent(current.user.id)}`, {
            method: current.included ? "PUT" : "DELETE", csrf: true,
          });
          if (!panel.current()) return;
          if (result?.user_id !== current.user.id || result.close_friend !== current.included) throw new Error("The change could not be confirmed.");
          members = members.filter(entry => entry.user.id !== current.user.id);
          if (current.included) members.push({ user: current.user });
          attempt = null;
          status.textContent = current.included ? `${current.user.display_name || current.user.handle} is on your Close Friends list.`
            : `${current.user.display_name || current.user.handle} was removed from Close Friends.`;
          say(status.textContent);
        } catch (error) {
          if (!panel.current()) return;
          const rejected = Number(error?.status) >= 400 && Number(error?.status) < 500 && Number(error?.status) !== 408;
          if (rejected && !current.uncertain) attempt = null;
          else current.uncertain = true;
          status.textContent = attempt ? "Change is unconfirmed. Retry the same change, or reload the saved list."
            : error?.message || "The change was not saved.";
          say(status.textContent, true);
        } finally { busy = false; paint(); }
      }
      add.addEventListener("click", () => {
        if (!panel.current() || !ready || busy || loading || attempt) return;
        pickPerson("Add to Close Friends", user => {
          if (!panel.current() || busy || loading || attempt) return;
          attempt = { user, included: true, uncertain: false };
          void change();
        }, { parent: panel, excluded: new Set(members.map(entry => entry.user.id)) });
      });
      await load();
    }

    // Highlights under the profile header, and a story ring on the avatar when something is live.
    function decorateProfile({ avatar: face, highlights, user, isSelf, current }) {
      if (!user) return;
      void renderHighlights(highlights, user, isSelf);
      if (!face) return;
      face.classList.remove("sv-has-story");
      face.removeAttribute("role");
      face.removeAttribute("tabindex");
      face.removeAttribute("aria-label");
      void checkAuthor(user.id).then(live => {
        if (!live || (current && !current())) return;
        face.classList.add("sv-has-story");
        face.setAttribute("role", "button");
        face.tabIndex = 0;
        face.setAttribute("aria-label", `Open ${user.display_name || user.handle}'s story`);
      });
    }

    // Whether a profile has something live for this viewer, including people they do not follow.
    async function checkAuthor(authorId) {
      if (!viewerId() || !authorId) return false;
      if (hasLiveStory(authorId)) return true;
      try {
        const result = await hooks.api(`/api/story-items/by-author/${encodeURIComponent(authorId)}`);
        return Array.isArray(result?.items) && result.items.length > 0;
      } catch (_) {
        return false;
      }
    }

    function syncIdentity() {
      if (postingOwner && !viewerId()) {
        const writes = [...recoveryWrites];
        recoveryReset = Promise.allSettled([recoveryReset, ...writes]).then(() => recovery.clear()).catch(() => {
          say("Story recovery copies could not be cleared. Clear this site's browser data on a shared device.", true);
        });
      }
      if (postingOwner !== viewerId()) { postingEpoch += 1; postingOwner = viewerId(); }
      if (createOwner !== viewerId()) {
        createRequest += 1; createOwner = viewerId(); filePicker?.remove(); filePicker = null;
      }
      if (state.composer && state.composer.owner !== viewerId()) state.composer.finish();
      if (highlightCreateOwner !== viewerId()) { highlightCreates.clear(); highlightCreateOwner = viewerId(); }
      for (const panel of [...accountSheets]) if (panel.owner !== viewerId()) panel.shell.dismiss();
      if (replyOwner !== viewerId()) {
        replyRecords.clear();
        replyOwner = viewerId();
      }
      if (pollOwner !== viewerId()) { pollRecords.clear(); pollOwner = viewerId(); }
      if (viewerRequestOwner !== viewerId()) {
        viewerRequest += 1;
        viewerRequestOwner = viewerId();
      }
      if (state.viewer && state.viewer.owner !== viewerId()) state.viewer.close();
      if (state.posting && state.posting.owner !== viewerId()) cancelPosting(state.posting);
      if (state.trayViewer && state.trayViewer !== viewerId()) {
        state.tray = [];
        state.trayViewer = null;
        state.viewer?.close();
      }
    }

    return {
      refreshTray, trayEntries, renderTray, trayButton, addStoryButton, ownStoryTile, hasLiveStory, checkAuthor, decorateProfile,
      openCreate, openComposer, posting: () => state.posting,
      openAuthor, openItem, openHighlight, openArchive, openCloseFriends, renderHighlights, syncIdentity, cancelViewer,
    };
  }

  return {
    create,
    _test: {
      clamp, relativeAge, itemDurationMs, stickerPayload, validateDraft, orderTray, safeUrl, fitsStage, storyFileProblem,
      TEXT_COLORS, MAX_ELEMENTS, publicationStore, validRecovery,
    },
  };
});
